// Contact detail — three-column layout matching DealDetail/CompanyDetail.
// Left: editable properties.   Center: notes.   Right: company link + their engagements.

// Hover text for the header's role chips — specifics over the generic
// ROLE_META hints, built from fields already on the serialized contact.
// (E.g. Jon Wilkinson carries BOTH 'referred' — someone logged him as an
// incoming referral — and 'referral_partner' — he's on the Brand Builder
// list. The tooltips explain each without hunting through the panels.)

// referral_date is a DATE kept in a timestamptz column: the referral modal
// writes it as midnight UTC, event attendance as noon UTC. Two traps when
// rendering it: formatting the instant in local time reads as the previous
// day anywhere in the US, and slicing the first ten characters of the API's
// string depends on which zone the API serialized in (UTC on prod, the
// machine's zone on a dev box). The UTC calendar day is right in both.
window.fmtReferralDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return window.fmtDate(d.toISOString().slice(0, 10));
};

const contactRoleDetails = (raw) => {
  if (!raw) return undefined;
  const fmt = (iso) => (iso ? window.fmtReferralDay(iso) : null);
  const referredBy = raw.referral_source_contact_name || raw.referral_source_user_name
    || raw.referral_maker || raw.referral_source_company_name;
  return {
    referred: 'Came in as a referral'
      + (raw.referral_date ? ` on ${fmt(raw.referral_date)}` : '')
      + (referredBy ? ` — referred by ${referredBy}` : ' — source unresolved')
      + (raw.referral_owner_name ? ` · owner ${raw.referral_owner_name}` : '')
      + '. Details in the Referral panel.',
    referral_partner: 'On the Brand Builder list — refers deals to IndustryPro. See the panel below.',
    gpp: 'Growth Partner Program member — see the GPP panel below.',
    seller: raw.company_name
      ? `${raw.company_name} has a seller-side engagement.` : undefined,
    buyer: raw.company_name
      ? `${raw.company_name} is buyer-side (buyer firm or PB/PEG buyer tags).` : undefined,
  };
};

// ── Device helpers (phone / iPad) ───────────────────────────────────────────
// Read window.AtlasDevice at render time — touch/phone don't change within a
// session, and every desktop return value below is exactly what it was.
const devNow = () => window.AtlasDevice || {};
// The referral / GPP modals' side-by-side field rows stack on a phone.
const phoneGrid = (cols) => (devNow().phone ? '1fr' : cols);
// …and size to the visible viewport, so the keyboard can't hide Save.
const phoneModalMaxH = () => (devNow().phone ? 'calc(var(--vvh, 100vh) - 40px)' : '88vh');
// iOS Safari's keyboard covers the bottom of the fixed backdrop without
// shrinking it, so a dialog centered in it keeps its footer (Save) under the
// keys. Pad the backdrop by the keyboard's height (--kb, device.jsx) so the
// dialog centers in what's still visible.
const phoneModalPad = () => (devNow().phone ? { paddingBottom: 'calc(20px + var(--kb, 0px))' } : null);
// On touch a phone number is a tel: link — a tap dials instead of opening
// the editor. The rest of the cell still opens the editor. undefined = EditableCell's own
// read view (desktop, or nothing dialable). A do-not-call person's links ask
// first (window.CallButton).
const touchPhoneView = (raw, doNotCall, name) => {
  if (!devNow().touch || !raw || !window.AtlasPhone || !window.CallButton) return undefined;
  if (!window.AtlasPhone.parsePhones(raw).length) return undefined;
  return <window.CallButton phone={raw} doNotCall={doNotCall} name={name} />;
};
// Desktop: the number stays click-to-edit; a small phone icon next to it is
// the call link (also asks first when do-not-call).
const deskCallIcon = (raw, doNotCall, name, fallback = null) => (
  !devNow().touch && window.CallButton
    ? <window.CallButton variant="icon" phone={raw} doNotCall={doNotCall} name={name} fallback={fallback} />
    : fallback
);

// Who someone is to IndustryPro (derived roles) → which program panels the
// page leads with. The rest render only once a record exists; their create
// actions sit in the header's ⋯ menu.
const PROGRAM_ROLE = { referral: 'referred', gpp: 'gpp', bb: 'referral_partner' };
// Roles that make "Log referral they made" a header button.
const REFERRAL_MAKER_ROLES = ['referral_partner', 'referral_source', 'gpp'];

// A create action asked for from outside the panel (the header ⋯ menu bumps
// a counter). Only a change after mount opens it — a panel remounting with
// an old count stays shut.
const useAskTick = (tick, open) => {
  const seen = React.useRef(tick);
  React.useEffect(() => {
    if (tick && tick !== seen.current) { seen.current = tick; open(); }
  }, [tick]);   // eslint-disable-line react-hooks/exhaustive-deps
};

// iPad: the three columns need ~1000px; below that the notes column used to
// collapse to nothing (overflow hidden). Measure the page's own width — the
// sidebar may or may not be open — and pick three, two or one column(s).
const tabletContactLayout = (w) => (w >= 1100 ? 'three' : w >= 700 ? 'two' : 'one');

const ContactDetail = ({ contactId, activeUser, setView, setDealId, setCompanyId, back }) => {
  const device = window.useDevice();
  // ?panel= / ?note= for this person (record_panels.jsx); above every early return.
  const panel = window.usePanelParam({ kind: 'contact', id: contactId });
  // Size of the columns area, measured only on iPad (desktop never re-renders
  // for it). The first read is synchronous in the ref callback and the
  // columns mount only after it, so a wrong first guess never mounts (and
  // fetches) every panel twice. The height sizes the 'two' layout's pinned
  // notes column.
  const [gridW, setGridW] = React.useState(0);
  const [gridH, setGridH] = React.useState(0);
  const gridRO = React.useRef(null);
  const measureGrid = React.useCallback((el) => {
    if (gridRO.current) { gridRO.current.disconnect(); gridRO.current = null; }
    if (!el || !device.tablet) return;
    const take = (w, h) => {
      setGridW((prev) => (prev === w ? prev : w));
      setGridH((prev) => (prev === h ? prev : h));
    };
    take(Math.round(el.clientWidth), Math.round(el.clientHeight));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      take(Math.round(r.width), Math.round(r.height));
    });
    ro.observe(el);
    gridRO.current = ro;
  }, [device.tablet]);
  const [contact,    setContact]    = React.useState(null);
  const [company,    setCompany]    = React.useState(null);
  const [companyEngs,setCompanyEngs]= React.useState(null);
  // Paged notes feed (defined in company_detail.jsx, shared across record pages).
  const { notes, total: notesTotal, loadingMore: notesLoadingMore,
          loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ } =
    window.usePagedNotes(contactId ? { contact_id: contactId } : null);
  const [users,      setUsers]      = React.useState([]);
  const [followUp,   setFollowUp]   = React.useState(false);
  // The Notes KPI is the feed's whole size (the total the Notes header
  // shows) — while a search narrows the feed it keeps the unsearched one.
  const [notesAll,   setNotesAll]   = React.useState(null);
  // Bumped when the header's Follow up closes, so the tasks card re-reads.
  const [tasksTick,  setTasksTick]  = React.useState(0);
  // Program panels that render only with a record report whether they
  // have one ({ events, gpp, bb }: true / false; absent = still loading);
  // the ⋯ menu offers the create action for the ones that don't.
  const [presence,   setPresence]   = React.useState({});
  const notePresence = React.useCallback((k, has) => {
    setPresence((p) => (p[k] === has ? p : { ...p, [k]: has }));
  }, []);
  // ⋯ menu → a panel's own create form (counters; see useAskTick).
  const [programAsk, setProgramAsk] = React.useState({ referral: 0, event: 0, gpp: 0, bb: 0 });
  const askProgram = (k) => setProgramAsk((a) => ({ ...a, [k]: a[k] + 1 }));
  const [loggingReferral, setLoggingReferral] = React.useState(false);
  // Another person: forget the last one's. Declared before the notesAll
  // effect so, in a commit where both run, the fresh total wins.
  React.useEffect(() => {
    setNotesAll(null); setPresence({}); setLoggingReferral(false);
  }, [contactId]);
  React.useEffect(() => {
    if (!notesQ && notesTotal != null) setNotesAll(notesTotal);
  }, [notesQ, notesTotal]);

  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
  }, []);

  // What "+ New" starts from on this page (app.jsx createPrefillFor): the
  // person, their company, and which side they're on.
  React.useEffect(() => {
    if (!contact || !window.publishPageContext) return undefined;
    const raw = contact._raw || {};
    window.publishPageContext({
      kind: 'contact', id: contact.id, name: contact.name, contactType: raw.contact_type || null,
      company: raw.company_id ? { id: raw.company_id, name: raw.company_name || contact.company || '' } : null,
    });
    return () => window.publishPageContext && window.publishPageContext(null, contact.id);
  }, [contact && contact.id, contact && contact.name, contact && contact._raw && contact._raw.company_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Phone: PhoneBurner's calls state (the do-not-call flag the Call button
  // checks) is asked for alongside the contact rather than after it, so it's
  // usually in hand before the header shows. ContactPhonePage takes it once.
  const callsPrefetch = React.useRef(null);   // { id, promise }

  React.useEffect(() => {
    let alive = true;
    setContact(null); setCompany(null); setCompanyEngs(null);
    callsPrefetch.current = window.AtlasDevice && window.AtlasDevice.phone
      ? { id: contactId, promise: window.AtlasApi.contactCalls(contactId).catch(() => null) }
      : null;
    (async () => {
      try {
        const raw = await window.AtlasApi.getContact(contactId);
        if (!alive) return;
        setContact(window.AtlasData.__adaptContact(raw, {}));
        if (raw.company_id) {
          const co = await window.AtlasApi.getCompany(raw.company_id).catch(() => null);
          if (alive && co) {
            setCompany(window.AtlasData.__adaptCompany(co));
            // Pull engagements from the same company — "where this person is involved" proxy
            // until we have a direct contact↔engagement link table.
            const engs = await window.AtlasApi.listEngagements({ company_id: raw.company_id, limit: 50 }).catch(() => null);
            if (alive) {
              setCompanyEngs(engs ? (engs.items || []).map(window.AtlasData.__adaptEngagement) : []);
            }
          } else if (alive) {
            setCompanyEngs([]);
          }
        } else if (alive) {
          setCompanyEngs([]);
        }
      } catch (e) {
        pushToast({ type: 'error', title: 'Could not load contact', body: e.message });
      }
    })();
    return () => { alive = false; };
  }, [contactId]);

  // Record-open beacon (api.jsx recordView): once per person. Never for a
  // deal guest.
  React.useEffect(() => {
    if (!contact || window.AtlasIsGuest || !window.AtlasApi.recordView) return;
    window.AtlasApi.recordView({
      page: 'contact', variant: 'person',
      device: device.kind, layout: window.recordLayout(), panel: 'none', action: 'open', via: 'load',
    });
  }, [contact ? contact.id : null]);  // eslint-disable-line react-hooks/exhaustive-deps

  // The Lead source / Event rows in the Company block edit the COMPANY.
  const saveCompanyLead = React.useCallback(async (patch) => {
    if (!company) return;
    try {
      const updated = await window.AtlasApi.updateCompany(company.id, patch);
      setCompany(window.AtlasData.__adaptCompany(updated));
      pushToast({ type: 'success', title: 'Saved' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [company]);

  // Re-read the record after a sub-resource change (additional emails,
  // merge) — those endpoints don't return the full contact.
  const reloadContact = React.useCallback(async () => {
    const fresh = await window.AtlasApi.getContact(contactId);
    setContact(window.AtlasData.__adaptContact(fresh, {}));
  }, [contactId]);

  const saveContact = React.useCallback(async (patch) => {
    try {
      const updated = await window.AtlasApi.updateContact(contactId, patch);
      setContact(window.AtlasData.__adaptContact(updated, {}));
      if ('company_id' in patch) {
        // Employer changed — re-anchor the company panel and the
        // same-company engagements list to the new company.
        const co = updated.company_id
          ? await window.AtlasApi.getCompany(updated.company_id).catch(() => null)
          : null;
        setCompany(co ? window.AtlasData.__adaptCompany(co) : null);
        const engs = co
          ? await window.AtlasApi.listEngagements({ company_id: updated.company_id, limit: 50 }).catch(() => null)
          : null;
        setCompanyEngs(engs ? (engs.items || []).map(window.AtlasData.__adaptEngagement) : []);
      }
      // An owner change that reached the book (one owner per book): the
      // company panel + same-company deals show owners, so re-pull them.
      const cas = updated && updated.owner_cascade;
      if (cas && (cas.companies || cas.engagements) && updated.company_id) {
        window.AtlasApi.getCompany(updated.company_id)
          .then((co) => setCompany(co ? window.AtlasData.__adaptCompany(co) : null)).catch(() => {});
        window.AtlasApi.listEngagements({ company_id: updated.company_id, limit: 50 })
          .then((engs) => setCompanyEngs((engs.items || []).map(window.AtlasData.__adaptEngagement))).catch(() => {});
      }
      pushToast({ type: 'success', title: 'Saved' });
      return updated;
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [contactId]);

  const typeOptions = [
    { value: 'seller',       label: 'Seller'   },
    { value: 'buyer',        label: 'Buyer'    },
    { value: 'gpp',          label: 'GPP'      },
    { value: 'professional', label: 'Professional services' },
    { value: 'internal',     label: 'Internal' },
  ];

  if (!contact) {
    if (device.phone) return <ContactPhoneSkeleton />;
    return <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading contact…</div>;
  }

  // PanelHost (record_panels.jsx): gate + host for ?panel= on both layouts.
  const panelFacts = { companyId: (contact._raw && contact._raw.company_id) || null, roles: (contact._raw && contact._raw.roles) || [],
    companySeller: !(contact._raw && contact._raw.company_id) ? false : company ? window.isSellerCompany(company, null, [contact]) : companyEngs !== null ? false : null };
  const panelCtx = { companyId: panelFacts.companyId, reloadNotes, reloadRecord: reloadContact, company,
    onCompanyChange: (raw) => setCompany(window.AtlasData.__adaptCompany(raw)),
    // All details on a phone (ContactDetailsBody); the v2 page spreads its own on top.
    saveContact, saveCompanyLead, users, typeOptions, onRefresh: reloadContact,
    onOpenCompany: () => { if (company && setCompanyId) setCompanyId(company.id); } };

  // Phone: its own stacked page over the same state and save paths.
  if (device.phone) {
    return (
      <>
        <ContactPhonePage
          contact={contact} company={company} companyEngs={companyEngs} users={users}
          activeUser={activeUser} contactId={contactId} callsPrefetch={callsPrefetch}
          notesFeed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes,
                       reload: reloadNotes, search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
          saveContact={saveContact} setContact={setContact} setCompany={setCompany}
          setView={setView} setDealId={setDealId} setCompanyId={setCompanyId}
        />
        <window.PanelHost kind="contact" id={contactId} record={contact} facts={panelFacts} variant="person" ctx={panelCtx} />
        <window.NoteHost kind="contact" id={contactId} onChange={reloadNotes} />
      </>
    );
  }

  const cr = contact._raw || {};
  const roles = cr.roles || [];
  const dnc = !!cr.do_not_call;
  // Merged-away duplicates (and deal guests) get no program create actions.
  const ctas = !cr.is_duplicate && !window.AtlasIsGuest;
  const roleApplies = (k) => roles.includes(PROGRAM_ROLE[k]);
  const hasReferral = !!(cr.referral_date || cr.referral_owner_id || cr.referral_source_kind);
  const logReferralInHeader = ctas && roles.some((r) => REFERRAL_MAKER_ROLES.includes(r));
  const menuItems = ctas ? [
    !roleApplies('referral') && !hasReferral && { key: 'referral', label: 'Mark as referred…', onSelect: () => askProgram('referral') },
    presence.events === false && { key: 'event', label: 'Record event…', onSelect: () => askProgram('event') },
    !roleApplies('gpp') && presence.gpp === false && { key: 'gpp', label: 'Promote to GPP…', onSelect: () => askProgram('gpp') },
    !roleApplies('bb') && presence.bb === false && { key: 'bb', label: 'Add to Brand Builder…', onSelect: () => askProgram('bb') },
    !logReferralInHeader && { key: 'logref', label: 'Log referral they made…', onSelect: () => setLoggingReferral(true) },
  ] : [];
  const sellerDeals = companyEngs ? companyEngs.filter((e) => e.type !== 'buyer_buyside') : null;

  // Wave D (step 13): the new layout, when the team's record layout is new
  // (Settings → Feature flags; an admin's ⌘K preview; never a phone — the surface is
  // 'phone' there, and the phone branch returned above). The v2 page renders
  // RecordPage, whose PanelHost and NoteHost replace the two host lines below;
  // the NewReferralDrawer is re-mounted by the v2 page from the same state
  // (views/contact_page_v2.jsx).
  if (window.recordSurface() === 'v2' && typeof window.ContactPageV2 === 'function') {
    return (
      <window.ContactPageV2 contactId={contactId} contact={contact} company={company} companyEngs={companyEngs}
        users={users} activeUser={activeUser} typeOptions={typeOptions} back={back}
        dnc={dnc} ctas={ctas} roles={roles}
        presence={presence} onPresence={notePresence} menuItems={menuItems} askProgram={askProgram} programAsk={programAsk}
        feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
        onSave={saveContact} onSaveCompany={saveCompanyLead} onReload={reloadContact}
        setContact={setContact} setCompany={setCompany} setCompanyEngs={setCompanyEngs}
        setDealId={setDealId} setCompanyId={setCompanyId}
        panel={{ facts: panelFacts, ctx: panelCtx }}
        drawers={{ loggingReferral, setLoggingReferral }} />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)' }}>
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px 0', fontSize: 11.5, color: 'var(--text-3)' }}>
          <button onClick={back} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5 }}>
            <Icon.ChevronL size={11} /> People
          </button>
          <Icon.ChevronR size={11} color="var(--text-4)" />
          <span>{contact.name}</span>
        </div>

        {/* Soft-merged away (POST /contacts/{keep}/merge): the row is hidden
            from lists and resolvers; anyone landing here from an old link
            gets pointed at the survivor. */}
        {contact._raw?.is_duplicate && contact._raw?.merged_into_id && (
          <div style={{ margin: '10px 16px 0', padding: '8px 12px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'var(--surface-2, var(--surface))',
                        fontSize: 12, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>This contact was merged into another record — its notes, tags and email addresses live there now.</span>
            <Btn size="xs" onClick={() => window.AtlasNav?.goToContact(contact._raw.merged_into_id)}>
              Open the surviving contact
            </Btn>
          </div>
        )}
        <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <Avatar name={contact.name} size={40} color="oklch(0.50 0.13 295)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em' }}>{contact.name}</h1>
              {/* The calling team marked them do not call (the Calls panel's
                  flag) — seen before anyone dials; call links ask first. */}
              {dnc && <Pill hue="rose" sz="sm" title="Do not call">DNC</Pill>}
              {/* Derived roles replace the old contact_type badge (which the
                  importer hardcoded to 'seller' for every synced contact). */}
              <window.RoleChips roles={contact._raw?.roles} details={contactRoleDetails(contact._raw)} />
              {/* The imported (DealCloud) contact type — hidden for the bulk
                  default that covers 92% of the mirror. */}
              {contact._raw?.dc_contact_type
                && contact._raw.dc_contact_type !== 'Seller: Primary Contact'
                && <Pill hue="slate" sz="sm" title="Imported type">{contact._raw.dc_contact_type}</Pill>}
              {contact._raw?.is_decision_maker && <Pill hue="green" sz="sm">decision-maker</Pill>}
              <window.SyncBadge
                lastSyncedAt={contact.lastSyncedAt} updatedAt={contact.updatedAt}
                dealcloudId={contact.dealcloudId || contact._raw?.dealcloud_id}
                recordType="contact" recordId={contactId}
                onSynced={async () => {
                  const fresh = await window.AtlasApi.getContact(contactId);
                  setContact(window.AtlasData.__adaptContact(fresh, {}));
                }}
              />
            </div>
            {/* Header meta — shown fields are click-to-edit in place (same
                EditableCell as the sidebar); empty fields stay hidden to keep
                the header quiet — add them from the properties panel. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4, fontSize: 12, color: 'var(--text-3)', flexWrap: 'wrap' }}>
              {contact.title && contact.title !== '—' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon.Tag size={12} />
                  <window.EditableCell type="text" value={contact._raw?.title || ''}
                    locked={Boolean(contact._raw?.dealcloud_id)}
                    onSave={(title) => saveContact({ title })} />
                </span>
              )}
              {company && <button onClick={() => setCompanyId && setCompanyId(company.id)} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: 'var(--accent-text)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
                textDecoration: 'underline', textUnderlineOffset: 2,
              }}><Icon.Building size={12} /> {company.name}</button>}
              {contact.email && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Icon.Mail size={12} />
                  <window.EditableCell type="email" value={contact._raw?.email || ''}
                    locked={Boolean(contact._raw?.dealcloud_id)}
                    onSave={(email) => saveContact({ email })} />
                </span>
              )}
              {(contact._raw?.emails || []).length > 0 && (
                <span title={(contact._raw.emails || []).map((a) => a.email).join('\n')}>
                  <Pill hue="slate" sz="sm">
                    +{contact._raw.emails.length} {contact._raw.emails.length === 1 ? 'email' : 'emails'}
                  </Pill>
                </span>
              )}
              {contact._raw?.phone && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {deskCallIcon(contact._raw.phone, dnc, contact.name, <Icon.Phone size={12} />)}
                  <window.EditableCell type="text" value={contact._raw?.phone || ''}
                    locked={Boolean(contact._raw?.dealcloud_id)}
                    displayAs={touchPhoneView(contact._raw?.phone, dnc, contact.name)}
                    onSave={(phone) => saveContact({ phone })} />
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {/* Referral partners / sources and GPP partners send people our
                way — logging one is their page's main action. */}
            {logReferralInHeader && window.NewReferralDrawer && (
              <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
                    title={`Record a referral ${contact.name} made`}
                    onClick={() => setLoggingReferral(true)}>
                Log referral they made
              </Btn>
            )}
            <Btn size="xs" variant="outline" active={followUp}
                  title={`Create a follow-up reminder for ${contact.name}`}
                  onClick={() => setFollowUp(!followUp)}>
              ⏰ Follow up
            </Btn>
            {window.RecordMenu && <window.RecordMenu items={menuItems} />}
          </div>
        </div>

        {followUp && (
          <div style={{ padding: '10px 16px 0', maxWidth: 640 }}>
            <window.FollowUpComposer
              seedTitle={`Follow up with ${contact.name}`}
              links={{ contact_id: contactId, company_id: contact._raw?.company_id }}
              linkedName={contact.name}
              onClose={() => { setFollowUp(false); setTasksTick((t) => t + 1); }} />
          </div>
        )}

        <div style={{ display: 'flex', padding: '14px 16px 0' }}>
          <DKpi label="Notes"          value={notesAll != null ? notesAll : (notes ? notes.length : '—')} />
          {/* Buyer-side pursuit shells are bookkeeping, not this company's
              deals — same count as the Engagements rail. */}
          <DKpi label="Deals at co."   value={sellerDeals ? sellerDeals.length : '—'} />
        </div>
        <div style={{ height: 14 }} />
      </div>

      {/* desktop: the three columns exactly as before. iPad: three, two or
          one column(s) by measured width (tabletContactLayout). */}
      {(() => {
        const leftCol = (
          <>
            <ContactPropertiesPanel
              contact={contact}
              company={company}
              users={users}
              typeOptions={typeOptions}
              onSave={saveContact}
              onSaveCompany={saveCompanyLead}
              onOpenCompany={() => company && setCompanyId && setCompanyId(company.id)}
              onRefresh={reloadContact}
            />
            <ContactTagsPanel contactId={contactId} activeUser={activeUser} />
          </>
        );
        const centerCol = (
          <>
            <ContactOpenTasks contact={contact} contactId={contactId} users={users}
              activeUser={activeUser} tick={tasksTick} />
            {/* Program panels: the ones this person's roles call for, plus any
                program they have a record in. The others render nothing (their
                create forms still mount here) and are started from the ⋯ menu.
                A flex gap, so a panel that renders nothing leaves no gap. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <ContactReferralPanel
                contact={contact}
                users={users}
                onSave={saveContact}
                setView={setView}
                setContactId={(id) => { if (id !== contactId) window.AtlasNav?.goToContact(id); }}
                setCompanyId={setCompanyId}
                optional={!roleApplies('referral')}
                cta={ctas}
                createTick={programAsk.referral}
              />
              {/* Lead events (142): which workshop / lunch-and-learn / webinar this
                  person was at, and who brought them. Recording one can stamp the
                  company's lead source and the referral block above, so the
                  contact is re-read afterwards. */}
              <window.ContactLeadEventsPanel
                contact={contact}
                activeUser={activeUser}
                optional
                cta={ctas}
                createTick={programAsk.event}
                onPresence={(has) => notePresence('events', has)}
                onChanged={async () => {
                  const fresh = await window.AtlasApi.getContact(contactId).catch(() => null);
                  if (fresh) setContact(window.AtlasData.__adaptContact(fresh, {}));
                  // Recording an attendance may have stamped the company's lead
                  // source too — the Company block's rows read from `company`.
                  if (fresh && fresh.company_id) {
                    const co = await window.AtlasApi.getCompany(fresh.company_id).catch(() => null);
                    if (co) setCompany(window.AtlasData.__adaptCompany(co));
                  }
                }}
              />
              <ContactGppPanel
                contact={contact}
                users={users}
                setContactId={(id) => { if (id !== contactId) window.AtlasNav?.goToContact(id); }}
                setCompanyId={setCompanyId}
                optional={!roleApplies('gpp')}
                cta={ctas}
                createTick={programAsk.gpp}
                onPresence={(has) => notePresence('gpp', has)}
              />
              <ContactOutreachPanel contact={contact} defaultOpen={false} />
              {/* "Log referral they made" lives in the header (or the ⋯ menu). */}
              <ContactReferralPartnerPanel
                contact={contact}
                users={users}
                activeUser={activeUser}
                optional={!roleApplies('bb')}
                cta={ctas}
                logReferral={false}
                createTick={programAsk.bb}
                onPresence={(has) => notePresence('bb', has)}
              />
            </div>
            <div style={{ height: 14 }} />
            {/* The brief of the company this person works at — what a caller
                reads before dialling them. Same brief as the company page,
                and like there only for a seller (null: this page doesn't load
                the buyer-roster row; the derived roles stand in; a GPP
                partner's own firm is not a seller). */}
            {contact._raw?.company_id && window.SellerBriefSection && window.isSellerCompany(company, null, [contact]) && (
              window.recordCardsOn('ContactCompanyCard') ? (
              <>
                <window.ContactCompanyCard contactId={contactId} companyId={contact._raw.company_id} companyName={contact._raw?.company_name || null} />
                <div style={{ height: 14 }} />
              </>
              ) : (
              <>
                <window.SellerBriefSection companyId={contact._raw.company_id} sectionId="contact.seller_brief"
                  title={`Seller brief · ${contact._raw?.company_name || 'their company'}`} />
                <div style={{ height: 14 }} />
                {window.CompsSection && (
                  <>
                    <window.CompsSection companyId={contact._raw.company_id} sectionId="contact.comps" />
                    <div style={{ height: 14 }} />
                  </>
                )}
              </>
              )
            )}
            {/* Cold calls (PhoneBurner) — history for everyone; the send button
                only for the calling team. Renders nothing when never called. */}
            <window.DialerCallsPanel contactId={contactId} />
            {window.WebsiteVisitsCard && <window.WebsiteVisitsCard contactId={contactId} variant="card" />}
            <window.RecordNotes kind="contact" recordId={contactId}
              feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes,
                reload: reloadNotes, search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
              attach={{ contact_id: contactId, company_id: contact._raw?.company_id || null }}
              activeUser={activeUser} members={users} engagementPicker label="Notes"
              taskLinks={{ contact: { id: contactId, name: contact.name },
                company: contact._raw?.company_id ? { id: contact._raw.company_id, name: contact._raw.company_name || 'Company' } : null }} />
          </>
        );
        const rightCol = <window.RelatedEngagementsPanel engagements={companyEngs} onOpen={(id) => setDealId(id)} />;
        // An iPad not measured yet renders the (measuring) body empty rather
        // than guess a layout and remount everything a frame later.
        const measured = !device.tablet || gridW > 0;
        const layout = device.tablet ? tabletContactLayout(gridW) : 'desktop';
        const line = '1px solid var(--border)';
        // The same three column elements in the same order in every layout —
        // only their styles change — so crossing an iPad breakpoint (rotation,
        // sidebar) never remounts a panel and drops a half-written note.
        let cols;
        if (layout === 'desktop' || layout === 'three') {
          const tpl = layout === 'desktop' ? '300px minmax(0, 1fr) 360px' : '280px minmax(0, 1fr) 320px';
          cols = {
            outer:  { flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: tpl, gap: 0 },
            left:   { borderRight: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' },
            center: { overflow: 'auto', padding: 16, background: 'var(--surface-2)' },
            right:  { borderLeft: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' },
          };
        } else if (layout === 'two') {
          // Notes column + one rail: properties on top, the company's deals
          // under them. The body scrolls the rail; the notes column is pinned
          // to the body's height with its own scroll, so the two still scroll
          // independently.
          cols = {
            outer:  { flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gridTemplateRows: 'auto 1fr', gap: 0,
                      overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
            left:   { gridColumn: 2, gridRow: 1, borderLeft: line, background: 'var(--surface)' },
            center: { gridColumn: 1, gridRow: '1 / 3', position: 'sticky', top: 0, alignSelf: 'start', height: gridH || '100%',
                      overflow: 'auto', overscrollBehavior: 'contain', padding: 16, background: 'var(--surface-2)', WebkitOverflowScrolling: 'touch' },
            right:  { gridColumn: 2, gridRow: 2, borderLeft: line, borderTop: line, background: 'var(--surface)' },
          };
        } else {
          // One column (Split View, or portrait with the sidebar open): a
          // single scroll — the notes, then properties + deals as one card.
          cols = {
            outer:  { flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', alignContent: 'start',
                      overflowX: 'hidden', overflowY: 'auto', background: 'var(--surface-2)', WebkitOverflowScrolling: 'touch' },
            center: { gridRow: 1, padding: 16 },
            left:   { gridRow: 2, margin: '0 16px', border: line, borderBottom: 'none', borderRadius: '10px 10px 0 0', background: 'var(--surface)', overflow: 'hidden' },
            right:  { gridRow: 3, margin: '0 16px 16px', border: line, borderRadius: '0 0 10px 10px', background: 'var(--surface)', overflow: 'hidden' },
          };
        }
        return (
          <div ref={measureGrid} style={measured ? cols.outer : { flex: 1, minHeight: 0 }}>
            {measured && (
              <>
                <div style={cols.left}>{leftCol}</div>
                <div style={cols.center}>{centerCol}</div>
                <div style={cols.right}>{rightCol}</div>
              </>
            )}
          </div>
        );
      })()}

      {/* A referral this person made (header button or ⋯ menu) — they are
          the source; a GPP partner's referral is marked GPP up front. */}
      {loggingReferral && window.NewReferralDrawer && (
        <window.NewReferralDrawer
          users={users}
          activeUser={activeUser}
          initialSource={{
            id: cr.id || contactId,
            full_name: cr.full_name || contact.name,
            company_id: cr.company_id || null,
            company_name: cr.company_name || null,
          }}
          initialIsGpp={roles.includes('gpp') || presence.gpp === true}
          onClose={() => setLoggingReferral(false)}
          onCreated={() => {
            setLoggingReferral(false);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />
      )}
      <window.PanelHost kind="contact" id={contactId} record={contact} facts={panelFacts} variant="person" ctx={panelCtx} />
      <window.NoteHost kind="contact" id={contactId} onChange={reloadNotes} />
    </div>
  );
};

// ── Phone: the person page ──────────────────────────────────────────────────
// ContactDetail branches here on a phone (same state, same save paths). The
// order follows the call workflow — who they are, call / text / email,
// how to reach them, what happened lately, what's next, their company's
// deals — and everything else folds away below. Phone numbers are plain
// tel: links here: editing lives in All details (?panel=details), so a tap
// on a number dials instead of opening the editor.

const cpTap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };
// window.AtlasPhone lives in mobile/people.jsx; guard so a load-order slip
// degrades to "no buttons" instead of a crash.
const cpPhoneKit = () => window.AtlasPhone || { parsePhones: () => [], contactEmails: () => [], MsgIcon: () => null };

const ContactPhoneSkeleton = () => (
  <div aria-hidden="true" style={{ height: '100%', overflow: 'hidden', background: 'var(--surface-2)' }}>
    <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '20px 16px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <Skeleton w={64} h={64} style={{ borderRadius: 32 }} />
      <Skeleton w="55%" h={20} />
      <Skeleton w="40%" h={13} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, width: '100%', marginTop: 10 }}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={62} style={{ borderRadius: 12, width: '100%' }} />)}
      </div>
    </div>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <Skeleton w={20} h={20} style={{ borderRadius: 10 }} />
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton w={`${50 + ((i * 17) % 35)}%`} h={13} />
          <Skeleton w={`${30 + ((i * 23) % 30)}%`} h={11} />
        </span>
      </div>
    ))}
  </div>
);

// One of the big buttons under the name: an <a> when there's a single
// target, a button that opens the picker when there are several. A link's
// onClick (the do-not-call confirm) may preventDefault to ask first.
const ContactPhoneAction = ({ icon, label, href, onClick, active }) => {
  const style = {
    ...cpTap, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
    minHeight: 62, borderRadius: 12, border: 'none', cursor: 'pointer', textDecoration: 'none',
    background: active ? 'var(--accent)' : 'var(--accent-soft)', color: active ? '#fff' : 'var(--accent-text)',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: '8px 4px',
  };
  const body = <>{icon}<span>{label}</span></>;
  return href
    ? <a href={href} onClick={onClick} style={style}>{body}</a>
    : <button type="button" onClick={onClick} aria-pressed={active ? true : undefined} style={style}>{body}</button>;
};

// A read-only "how to reach them" row: the whole row is the link.
const ContactPhoneLinkRow = ({ icon, title, subtitle, href, onClick, external, trailing }) => (
  <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
    <a href={href} onClick={onClick} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}
      style={{ ...cpTap, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 14, minHeight: 56, padding: '9px 8px 9px 16px', textDecoration: 'none', color: 'var(--text)' }}>
      <span style={{ display: 'inline-flex', color: 'var(--text-3)', flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 15.5, color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {subtitle && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', marginTop: 1 }}>{subtitle}</span>}
      </span>
    </a>
    {trailing}
  </div>
);

// Open tasks about this person (and company-wide ones at their company).
// /api/tasks filters by company, not contact, so the company's open list
// is narrowed here; a person with no company has nothing to query. One
// query for the phone list (window.RecordTasks, views/record_tasks.jsx) and
// the desktop card (ContactOpenTasks).
const useContactOpenTasks = (companyId, contactId) => {
  const [tasks, setTasks] = React.useState(null);   // null = loading
  const [error, setError] = React.useState(null);
  const reload = React.useCallback(async () => {
    if (!companyId) { setTasks([]); return; }
    try {
      const res = await window.AtlasApi.listTasks({ company_id: companyId, status: 'open', limit: 100 });
      const items = ((res && res.items) || []).filter((t) => !t.contact_id || String(t.contact_id) === String(contactId));
      items.sort((a, b) => String(a.due_date || '').localeCompare(String(b.due_date || '')));
      setTasks(items);
      setError(null);
    } catch (e) {
      setTasks([]);
      setError(e.message || 'Could not load tasks');
    }
  }, [companyId, contactId]);
  React.useEffect(() => { setTasks(null); reload(); }, [reload]);
  return { tasks, error, reload };
};

// Desktop: "Open tasks · N" at the top of the center column — overdue
// first, a tick completes (a recurring task schedules its next one, as on
// the phone), a click opens the task. Nothing renders while none are open;
// `showEmpty` (the v2 page, for a writer) keeps it at 0 with a dimmed — row
// when a task added here can list (the person has a company) and the read
// didn't fail (a failed read isn't 0). The header's
// Follow up creates one (`tick` re-reads after it closes).
const ContactOpenTasks = ({ contact, contactId, users, activeUser, tick = 0, showEmpty = false }) => {
  const raw = contact._raw || {};
  const companyId = raw.company_id || null;
  const { tasks, error, reload } = useContactOpenTasks(companyId, contactId);
  const { toggle, stateFor } = window.useTaskToggle(reload);
  const [editing, setEditing] = React.useState(null);   // a task, or { create: true }
  useAskTick(tick, reload);
  // A task made from "+ New" / ⌘K (prefilled with this person) shows up too.
  React.useEffect(() => {
    window.addEventListener('task-created', reload);
    return () => window.removeEventListener('task-created', reload);
  }, [reload]);
  const guest = !!window.AtlasIsGuest;
  const canAdd = !!window.TaskDrawer && !guest;
  const today = window.localISODate();   // local day — a UTC "today" flips early in US evenings

  const drawer = editing && window.TaskDrawer ? (
    <window.TaskDrawer
      activeUser={activeUser}
      members={users}
      task={editing.create ? undefined : editing}
      initialLinks={editing.create ? {
        contact: { id: contactId, name: contact.name },
        company: companyId ? { id: companyId, name: raw.company_name || contact.company || 'Company' } : null,
      } : undefined}
      onClose={() => setEditing(null)}
      onCreated={reload}
    />
  ) : null;
  if (!tasks || (tasks.length === 0 && !(showEmpty && canAdd && companyId && !error))) return drawer;

  // Overdue, then due soonest, then undated.
  const rank = (t) => (!t.due_date ? 2 : t.due_date < today ? 0 : 1);
  const rows = [...tasks].sort((a, b) => (rank(a) - rank(b))
    || String(a.due_date || '').localeCompare(String(b.due_date || '')));

  return (
    <>
      <Section
        title={`Open tasks · ${tasks.length}`}
        style={{ marginBottom: 14 }}
        action={canAdd ? (
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} disabled={!activeUser}
            onClick={() => setEditing({ create: true })}>Task</Btn>
        ) : null}
      >
        {rows.length === 0 && (
          <div data-task-empty="" style={{ padding: '7px 14px', fontSize: 12.5, color: 'var(--text-4)' }}>—</div>
        )}
        {rows.map((t, i) => {
          const st = stateFor(t.id);
          const done = !!st;
          const overdue = !done && t.due_date && t.due_date < today;
          const who = [t.contact_id ? null : 'Company-wide', t.assigned_to_name].filter(Boolean).join(' · ');
          const meta = st && st.phase === 'done' && st.spawnedDue
            ? `Next one ${window.fmtTaskDue(st.spawnedDue)}`
            : [t.due_date ? `${overdue ? 'Overdue · ' : 'Due '}${window.fmtTaskDue(t.due_date)}` : null, who].filter(Boolean).join(' · ');
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
                                     borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <button type="button" disabled={guest}
                aria-label={done ? 'Undo complete' : `Complete “${t.title}”`}
                title={done ? 'Undo' : 'Complete'}
                onClick={() => toggle(done ? { ...t, status: 'completed' } : t)}
                style={{ width: 16, height: 16, flexShrink: 0, padding: 0, borderRadius: 8, cursor: guest ? 'default' : 'pointer',
                         border: `1.5px solid ${done ? 'var(--accent)' : 'var(--border-strong)'}`,
                         background: done ? 'var(--accent)' : 'transparent', color: '#fff',
                         display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {done && <Icon.Check size={10} />}
              </button>
              <button type="button" onClick={() => window.TaskDrawer && setEditing(t)}
                style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 10, padding: 0, border: 'none',
                         background: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                               textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--text-3)' : 'var(--text)' }}>
                  {t.title}
                </span>
                {meta && (
                  <span style={{ flexShrink: 0, fontSize: 11, color: overdue ? 'var(--rose-fg)' : 'var(--text-3)' }}>{meta}</span>
                )}
              </button>
            </div>
          );
        })}
      </Section>
      {drawer}
    </>
  );
};

const ContactPhonePage = ({
  contact, company, companyEngs, users, activeUser, contactId, callsPrefetch, notesFeed,
  saveContact, setContact, setCompany,
  setView, setDealId, setCompanyId,
}) => {
  const raw = contact._raw || {};
  const kit = cpPhoneKit();
  const phones = kit.parsePhones(raw.phone);
  const emails = kit.contactEmails(raw);
  const MsgIcon = kit.MsgIcon;
  const [pick, setPick] = React.useState(null);        // { title, items } — number / address picker
  const [followUp, setFollowUp] = React.useState(false);
  // Calls (PhoneBurner): the panel renders nothing for most people, so ask
  // first and only show the fold when there is something to open.
  // undefined = still loading; null = nothing (or the request failed).
  const [calls, setCalls] = React.useState(undefined);
  const callsReq = React.useRef(null);   // the in-flight/settled request for this contact
  React.useEffect(() => {
    let alive = true;
    setCalls(undefined);
    const pre = callsPrefetch && callsPrefetch.current;
    if (callsPrefetch) callsPrefetch.current = null;
    const req = pre && pre.id === contactId ? pre.promise : window.AtlasApi.contactCalls(contactId).catch(() => null);
    callsReq.current = req;
    req.then((d) => { if (alive) setCalls(d || null); });
    return () => { alive = false; callsReq.current = null; };
  }, [contactId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const callCount = calls ? (calls.calls || []).length : 0;
  const callsState = (calls && calls.state) || {};
  const showCalls = !!calls && (callCount > 0 || calls.can_send || calls.can_queue
    || callsState.do_not_call || callsState.bad_phone || callsState.sent_at || callsState.queued);

  // Do not call: the calling team dispositioned a PhoneBurner call "Do not
  // call" — the person asked not to be called. It's a warning, not a block
  // (a dealmaker may own the relationship): Call / Text and every number
  // on the page ask once, then hand off to the same tel:/sms: link. A tap
  // before the calls fetch lands waits for it (a quick tap on a slow
  // network must still get the warning); only the latest tap acts.
  const callsLoaded = calls !== undefined;
  const dnc = !!callsState.do_not_call;
  const [dncAsk, setDncAsk] = React.useState({ open: false, kind: 'call', href: '' });
  const askDnc = (kind, href) => setDncAsk({ open: true, kind, href });
  const closeDnc = () => setDncAsk((a) => ({ ...a, open: false }));
  const tapSeq = React.useRef(0);
  const callOrAsk = (kind, href) => {
    const seq = ++tapSeq.current;
    const req = callsReq.current;
    if (callsLoaded || !req) { if (dnc) askDnc(kind, href); else window.location.href = href; return; }
    req.then((d) => {
      if (callsReq.current !== req || seq !== tapSeq.current) return;   // left the page, or tapped again
      if (d && d.state && d.state.do_not_call) askDnc(kind, href); else window.location.href = href;
    });
  };
  // onClick for a tel:/sms: <a>: undefined (a plain link) once the person
  // is known not to be marked; otherwise hold the link and ask (or wait).
  const guardLink = (kind, href) => (callsLoaded && !dnc ? undefined : (e) => { e.preventDefault(); callOrAsk(kind, href); });
  const dncName = ((raw.first_name || '').trim() || (contact.name || '').trim().split(/\s+/)[0] || '').replace(/^—$/, '') || 'this person';

  const goCompany = () => company && setCompanyId && setCompanyId(company.id);
  const openDetails = () => window.openRecordPanel('details', {}, { via: 'button' });
  const goContact = (id) => { if (id !== contactId) window.AtlasNav?.goToContact(id); };

  // Picker for several numbers / addresses. Navigating to tel:/sms:/mailto:
  // hands off to the OS; the sheet closes behind it. A call or text to a
  // do-not-call person goes through the confirm first.
  const openPicker = (title, items) => setPick({ title, items });
  const handOff = (kind, href) => {
    if (kind === 'call' || kind === 'text') callOrAsk(kind, href);
    else window.location.href = href;
  };
  const pickerAction = (kind) => {
    if (kind === 'call') {
      if (phones.length === 1) return { href: phones[0].tel, onClick: guardLink('call', phones[0].tel) };
      return { onClick: () => openPicker(`Call ${contact.name}`, phones.map((p) => ({ key: p.tel, kind: 'call', label: p.display, sub: p.label, href: p.tel, icon: <Icon.Phone size={18} /> }))) };
    }
    if (kind === 'text') {
      if (phones.length === 1) return { href: phones[0].sms, onClick: guardLink('text', phones[0].sms) };
      return { onClick: () => openPicker(`Text ${contact.name}`, phones.map((p) => ({ key: p.sms, kind: 'text', label: p.display, sub: p.label, href: p.sms, icon: <MsgIcon size={18} /> }))) };
    }
    if (emails.length === 1) return { href: emails[0].href };
    return { onClick: () => openPicker(`Email ${contact.name}`, emails.map((e) => ({ key: e.href, kind: 'email', label: e.email, sub: e.label, href: e.href, icon: <Icon.Mail size={18} /> }))) };
  };
  const actions = [
    phones.length > 0 && { key: 'call', label: 'Call', icon: <Icon.Phone size={20} />, ...pickerAction('call') },
    phones.length > 0 && { key: 'text', label: 'Text', icon: <MsgIcon size={20} />, ...pickerAction('text') },
    emails.length > 0 && { key: 'email', label: 'Email', icon: <Icon.Mail size={20} />, ...pickerAction('email') },
    { key: 'follow', label: 'Follow up', icon: <Icon.Clock size={20} />, onClick: () => setFollowUp((f) => !f), active: followUp },
  ].filter(Boolean);

  const subtitle = (raw.title || '').trim();
  const roles = raw.roles || [];
  const roleDetails = contactRoleDetails(raw) || {};
  const lastTouch = raw.last_interaction_at ? window.relativeTime(raw.last_interaction_at) : null;

  const deals = companyEngs && companyEngs.filter((e) => e.type !== 'buyer_buyside')
    .sort((a, b) => (a.archived ? 1 : 0) - (b.archived ? 1 : 0));
  const shellCount = companyEngs ? companyEngs.length - deals.length : 0;

  const programsPreview = [
    roles.includes('referred') && 'Referred',
    roles.includes('gpp') && 'GPP',
    roles.includes('referral_partner') && 'Brand Builder',
  ].filter(Boolean).join(' · ') || 'Referral, events, GPP, tags';

  return (
    <window.PhoneScroll style={{ background: 'var(--surface-2)' }}>
      {/* The desktop Section headers inside the page (Outreach, Notes, brief)
          get a finger-sized header and phone-size titles. Scoped to this page. */}
      <style>{`
        .cp-page section > header { min-height: 48px; }
        .cp-page section > header h3 { font-size: 15px !important; }
      `}</style>
      <div className="cp-page">
        {raw.is_duplicate && raw.merged_into_id && (
          <button type="button" onClick={() => window.AtlasNav?.goToContact(raw.merged_into_id)}
            style={{ ...cpTap, display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 14, lineHeight: 1.4, cursor: 'pointer', fontFamily: 'inherit' }}>
            This contact was merged into another record. <span style={{ fontWeight: 600, textDecoration: 'underline' }}>Open the surviving contact</span>
          </button>
        )}

        {/* Who they are */}
        <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '18px 16px 16px', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Avatar name={contact.name} size={64} color="oklch(0.50 0.13 295)" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em', marginTop: 10, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{contact.name}</h1>
          {subtitle && <div style={{ fontSize: 15, color: 'var(--text-2)', marginTop: 3 }}>{subtitle}</div>}
          {company ? (
            <button type="button" onClick={goCompany}
              style={{ ...cpTap, display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '4px 8px', marginTop: 0, border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
              <Icon.Building size={15} /> {company.name}
            </button>
          ) : raw.company_name ? (
            <div style={{ fontSize: 15, color: 'var(--text-2)', marginTop: 4 }}>{raw.company_name}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <window.RoleChips roles={roles} details={roleDetails} />
            {raw.is_decision_maker && <Pill hue="green" sz="sm">decision-maker</Pill>}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6, lineHeight: 1.45 }}>
            {[raw.contact_owner_name ? `Owner ${raw.contact_owner_name}` : 'No owner', lastTouch && lastTouch !== '—' ? `last touch ${lastTouch}` : null].filter(Boolean).join(' · ')}
          </div>
          {/* The role chips' explanations are hover text on desktop — the
              referral one carries real facts (when, who), so say it here. */}
          {roles.includes('referred') && roleDetails.referred && (
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.45 }}>
              {roleDetails.referred.replace(/ Details in the Referral panel\.$/, '')}
            </div>
          )}

          {/* Seen before anyone taps Call or Text. */}
          {dnc && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <Pill hue="rose" dot>Do not call</Pill>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))`, gap: 8, marginTop: dnc ? 8 : 16 }}>
            {actions.map(({ key, ...a }) => <ContactPhoneAction key={key} {...a} />)}
          </div>
        </div>

        {followUp && (
          <div style={{ padding: '12px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <window.FollowUpComposer
              seedTitle={`Follow up with ${contact.name}`}
              links={{ contact_id: contactId, company_id: raw.company_id }}
              linkedName={contact.name}
              onClose={() => setFollowUp(false)} />
          </div>
        )}

        {/* How to reach them — read-only links; editing is in All details. */}
        <window.PhoneSectionHeader title="Contact" />
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {phones.map((p) => (
            <ContactPhoneLinkRow key={p.tel} icon={<Icon.Phone size={18} />} title={p.display} subtitle={p.label || 'phone'} href={p.tel}
              onClick={guardLink('call', p.tel)}
              trailing={(
                <a href={p.sms} onClick={guardLink('text', p.sms)} aria-label={`Text ${p.display}`}
                  style={{ ...cpTap, width: 52, height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-text)' }}>
                  <MsgIcon size={19} />
                </a>
              )} />
          ))}
          {/* DC phone text with nothing dialable in it ("." (Work)) — show
              it so it can be fixed, but not as a dead link. */}
          {!phones.length && raw.phone && raw.phone.trim() && (
            <window.PhoneRow leading={<Icon.Phone size={18} />} title={raw.phone.trim()} subtitle="not a dialable number" chevron={false} />
          )}
          {emails.map((e) => (
            <ContactPhoneLinkRow key={e.href} icon={<Icon.Mail size={18} />} title={e.email} subtitle={e.label === 'primary' ? 'email' : e.label} href={e.href} />
          ))}
          {raw.linkedin_url && (
            <ContactPhoneLinkRow icon={<Icon.Globe size={18} />} title="LinkedIn" subtitle={raw.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
              href={/^https?:\/\//.test(raw.linkedin_url) ? raw.linkedin_url : `https://${raw.linkedin_url}`} external />
          )}
          {[raw.city, raw.state].filter(Boolean).length > 0 && (
            <window.PhoneRow leading={<Icon.Map size={18} />} title={[raw.city, raw.state, raw.country && raw.country !== 'United States' ? raw.country : null].filter(Boolean).join(', ')} chevron={false} />
          )}
          {!phones.length && !raw.phone && !emails.length && (
            <window.PhoneRow leading={<Icon.Plus size={18} />} title="Add phone or email" onClick={openDetails} />
          )}
        </div>

        {/* What happened lately + log a note — the shared feed and composer
            (its own header carries the count, search and Add note). */}
        <div style={{ padding: '14px 10px 0' }}>
          <window.RecordNotes kind="contact" recordId={contactId} feed={notesFeed}
            attach={{ contact_id: contactId, company_id: raw.company_id || null }}
            activeUser={activeUser} members={users} engagementPicker label="Notes"
            taskLinks={{ contact: { id: contactId, name: contact.name },
              company: raw.company_id ? { id: raw.company_id, name: raw.company_name || 'Company' } : null }} />
        </div>

        {window.RecordTasks && (
          <window.RecordTasks kind="contact" presentation="phone" contact={contact} contactId={contactId}
            users={users} activeUser={activeUser} canWrite={!window.AtlasIsGuest} />
        )}

        <window.PhoneSectionHeader title={company ? `Deals at ${company.name}` : 'Deals'} count={deals && deals.length ? deals.length : null} />
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {deals === null && <SkeletonRows rows={2} />}
          {deals && deals.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: 14, color: 'var(--text-3)', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
              {raw.company_id ? 'No deals with this company as the client.' : 'Not linked to a company.'}
            </div>
          )}
          {deals && deals.map((e) => (
            <window.PhoneRow key={e.id} onClick={() => setDealId && setDealId(e.id)} dim={e.archived}
              title={e.name}
              subtitle={[e.stageName || e.stage, e.onHold ? 'on hold' : null, e.archived ? 'archived' : null, e.owner && e.owner !== '—' ? e.owner : null].filter(Boolean).join(' · ')}
              meta={e.size ? window.fmtMoney(e.size) : null} />
          ))}
          {shellCount > 0 && (
            <div style={{ padding: '8px 16px', fontSize: 12.5, color: 'var(--text-4)' }}>
              + {shellCount} buyer-side pursuit{shellCount === 1 ? '' : 's'} — on the company page
            </div>
          )}
        </div>

        {/* Folded: open when needed. */}
        <div style={{ padding: '14px 10px 0' }}>
          {/* Its own collapse key + folded by default, so the desktop
              page's remembered Outreach state is never read or written. */}
          <ContactOutreachPanel contact={contact} sectionId="phone.contact.outreach" defaultOpen={false} />
        </div>
        {showCalls && (
          <window.LazyFold id="phone.contact.calls" variant="card" title="Calls" preview={callCount ? `${callCount} call${callCount === 1 ? '' : 's'}` : (callsState.queued ? 'marked to call' : '')}>
            <div style={{ padding: 10 }}><window.DialerCallsPanel contactId={contactId} /></div>
          </window.LazyFold>
        )}
        {raw.company_id && window.SellerBriefSection && window.isSellerCompany(company, null, [contact]) && (
          <div style={{ padding: '10px 10px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <window.SellerBriefSection companyId={raw.company_id} sectionId="phone.contact.seller_brief"
              title={`Seller brief · ${raw.company_name || 'their company'}`} />
            {window.CompsSection && <window.CompsSection companyId={raw.company_id} sectionId="phone.contact.comps" />}
          </div>
        )}
        <window.LazyFold id="phone.contact.programs" variant="card" title="Referral & programs" preview={programsPreview}>
          <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ContactReferralPanel contact={contact} users={users} onSave={saveContact} setView={setView}
              setContactId={goContact} setCompanyId={setCompanyId} />
            <window.ContactLeadEventsPanel
              contact={contact}
              activeUser={activeUser}
              onChanged={async () => {
                // Same re-read as desktop: an attendance can stamp the
                // referral block and the company's lead source.
                const fresh = await window.AtlasApi.getContact(contactId).catch(() => null);
                if (fresh) setContact(window.AtlasData.__adaptContact(fresh, {}));
                if (fresh && fresh.company_id) {
                  const co = await window.AtlasApi.getCompany(fresh.company_id).catch(() => null);
                  if (co) setCompany(window.AtlasData.__adaptCompany(co));
                }
              }}
            />
            <ContactGppPanel contact={contact} users={users} setContactId={goContact} setCompanyId={setCompanyId} />
            <ContactReferralPartnerPanel contact={contact} users={users} activeUser={activeUser} />
            <ContactTagsPanel contactId={contactId} activeUser={activeUser} />
          </div>
        </window.LazyFold>
        {/* The full editable record (phones and emails too): the All details sheet. */}
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border)' }}>
          <window.PhoneRow title="All details" onClick={openDetails} />
        </div>
        <div style={{ height: 24 }} />
      </div>

      <window.PhoneSheet open={!!pick} onClose={() => setPick(null)} title={pick ? pick.title : ''}>
        <window.PhoneActionList items={((pick && pick.items) || []).map((it) => ({
          ...it,
          onClick: () => { setPick(null); handOff(it.kind, it.href); },
        }))} />
      </window.PhoneSheet>

      {/* Do-not-call confirm: asks, never blocks. Above the picker, which
          is still sliding away when this opens. */}
      <window.PhoneSheet open={dncAsk.open} onClose={closeDnc} zIndex={130} title="Marked do not call">
        <div style={{ padding: '0 18px 12px', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.45 }}>
          The calling team marked {dncName} as do not call. {dncAsk.kind === 'text' ? 'Text anyway?' : 'Call anyway?'}
        </div>
        <window.PhoneActionList items={[
          { key: 'go', label: dncAsk.kind === 'text' ? 'Text anyway' : 'Call anyway',
            icon: dncAsk.kind === 'text' ? <MsgIcon size={18} /> : <Icon.Phone size={18} />,
            onClick: () => { closeDnc(); window.location.href = dncAsk.href; } },
          { key: 'cancel', label: 'Cancel', onClick: closeDnc },
        ]} />
      </window.PhoneSheet>
    </window.PhoneScroll>
  );
};

// Per-contact tags. The tag catalog + auto-classifier already exist (Settings →
// Custom Tags); this exposes the last-mile: applying/removing a tag on one
// person. Add/remove endpoints return the full list so we swap state directly.
const ContactTagsPanel = ({ contactId, activeUser }) => {
  const [tags, setTags] = React.useState(null);   // null = loading
  const [catalog, setCatalog] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setTags(null);
    window.AtlasApi.listContactTags(contactId)
      .then((res) => { if (alive) setTags(Array.isArray(res) ? res : []); })
      .catch(() => { if (alive) setTags([]); });
    return () => { alive = false; };
  }, [contactId]);

  const openPicker = () => {
    setAdding(true);
    if (catalog === null) {
      // Non-archived catalog; searchable in the combobox. ~3K rows load once
      // (5000 is the API's max — anything below it silently drops tags).
      window.AtlasApi.listTags({ limit: 5000 })
        .then((res) => setCatalog((res && res.items) || []))
        .catch(() => setCatalog([]));
    }
  };

  const add = async (tagId) => {
    if (!tagId) return;
    setBusy(true);
    try {
      const res = await window.AtlasApi.addContactTag(contactId, tagId);
      setTags(Array.isArray(res) ? res : []);
      setAdding(false);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add tag', body: e.message });
    } finally { setBusy(false); }
  };

  const remove = async (tagId) => {
    setBusy(true);
    try {
      const res = await window.AtlasApi.removeContactTag(contactId, tagId);
      setTags(Array.isArray(res) ? res : []);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not remove tag', body: e.message });
    } finally { setBusy(false); }
  };

  const applied = new Set((tags || []).map((t) => String(t.tag_id)));
  const pickerOptions = [
    { value: '', label: 'Select a tag…' },
    ...((catalog || [])
      .filter((c) => !applied.has(String(c.id)))
      .map((c) => ({ value: String(c.id), label: c.name }))),
  ];

  return (
    <Section
      title={`Tags${tags ? ` · ${tags.length}` : ''}`}
      action={!adding && (
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
          onClick={openPicker} disabled={!activeUser || busy}>Add tag</Btn>
      )}
    >
      <div style={{ padding: '10px 14px' }}>
        {tags === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {tags && tags.length === 0 && !adding && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No tags yet.</div>
        )}
        {tags && tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tags.map((t) => (
              <span key={t.tag_id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px',
                fontSize: 11.5, borderRadius: 999, background: 'var(--surface-2)',
                border: '1px solid var(--border)', color: 'var(--text-2)',
              }}>
                {t.name}
                <button onClick={() => remove(t.tag_id)} disabled={busy} title="Remove tag"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-4)', display: 'inline-flex', padding: 0, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
                  {window.PhoneHit && <window.PhoneHit />}
                  <Icon.Close size={10} />
                </button>
              </span>
            ))}
          </div>
        )}
        {adding && (
          <div style={{ marginTop: tags && tags.length ? 8 : 0, display: 'flex', gap: 6, alignItems: 'center' }}>
            <Combobox
              value=""
              searchable
              onChange={(e) => add(e.target.value)}
              options={pickerOptions}
              placeholder={catalog === null ? 'Loading…' : 'Search tags…'}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Btn size="xs" variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>
          </div>
        )}
      </div>
    </Section>
  );
};

// Company row with a "change" affordance — the one contact field EditableCell
// can't cover (needs the server-search picker, not a fixed option list).
// The edit mode is the same picker the New person drawer uses: a Combobox
// that opens on mount with a starter list (my team's companies) and searches
// the full mirror as you type, plus a "+ New company" row pinned under the
// results that opens NewCompanyDrawer (seeded with the query) and links the
// created record to this contact.
// editable=false (a viewer on the v2 page) drops the change / set company link.
const CompanyRowEditor = ({ contact, company, onSave, onOpenCompany, editable = true }) => {
  const [mode, setMode] = React.useState('idle'); // idle | editing
  const [creating, setCreating] = React.useState(false);
  const [lookups, setLookups] = React.useState(null);  // {industries, members} — lazy
  const [seedName, setSeedName] = React.useState('');   // picker query → drawer's Name
  const startEdit = () => setMode('editing');

  const link = async (companyId) => {
    if (companyId && companyId !== contact._raw?.company_id) {
      try { await onSave({ company_id: companyId }); } catch { /* toast upstream */ }
    }
    setMode('idle');
  };

  const openCreate = (seed) => {
    setSeedName(seed || '');
    if (lookups === null) {
      Promise.all([
        window.AtlasApi.listIndustries().catch(() => []),
        window.AtlasData.loadTeamMembers().catch(() => []),
      ]).then(([industries, members]) => setLookups({
        industries: Array.isArray(industries) ? industries : [],
        members: Array.isArray(members) ? members : [],
      }));
    }
    setCreating(true);
  };

  const current = company
    ? (
      <button onClick={onOpenCompany} style={{
        background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        color: 'var(--accent-text)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, ...(window.phoneHitHost ? window.phoneHitHost() : null),
      }}>
        {window.PhoneHit && <window.PhoneHit />}
        <Logo name={company.name} color={company.logo} size={16} />
        {company.name}
      </button>
    )
    : <span style={{ color: 'var(--text-4)' }}>—</span>;

  if (mode === 'editing') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Combobox autoOpen value="" placeholder="Search companies…" options={[]}
          loadOptions={window.searchCompanyOptions}
          onChange={(e) => { if (e.target.value) link(e.target.value); }}
          footer={({ query, close }) => (
            <button type="button" onClick={() => { close(); openCreate(query); }}
              title="Create a company that isn't in Atlas yet and link it to this contact"
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                        padding: '7px 10px', border: 'none', textAlign: 'left',
                        background: 'transparent', cursor: 'pointer',
                        color: 'var(--accent-text)', fontSize: 12, fontWeight: 500 }}>
              <Icon.Plus size={10} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                New company{query ? ` “${query}”` : ''}
              </span>
            </button>
          )} />
        <button onClick={() => setMode('idle')} style={{
          alignSelf: 'flex-start', background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text-3)', fontSize: 11, padding: 0,
        }}>Cancel</button>
        {creating && ReactDOM.createPortal(
          <window.NewCompanyDrawer
            industries={lookups?.industries || []}
            members={lookups?.members || []}
            initialName={seedName}
            onClose={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false);
              if (created?.id) link(created.id);
              window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            }}
          />, document.body)}
      </div>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {current}
      {editable && (
        <button onClick={startEdit}
          title="Move this contact to a different company"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--text-3)', fontSize: 11, padding: 0, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
          {window.PhoneHit && <window.PhoneHit />}
          {company ? 'change' : 'set company'}
        </button>
      )}
    </span>
  );
};

// ============ Additional emails + merge ============
//
// One person, several addresses. contacts.email stays the DealCloud primary;
// the rows below are Atlas-only aliases (contact_emails) that every
// address→contact resolver honours — the Outlook/Fireflies syncs, the Atlas
// Inbox, marketing ingest, Cmd-K. Adding one also back-links synced notes
// that mentioned the address but had no contact yet.
//
// apiFetch throws `Error("<status> <text>: <json body>")`; these two pull
// the status and the JSON `detail` back out so a 409 can carry the other
// contact for the merge offer.
const apiErrStatus = (e) => parseInt(String(e?.message || '').slice(0, 3), 10) || 0;
const apiErrDetail = (e) => {
  try {
    const m = String(e?.message || '');
    const i = m.indexOf('{');
    return i >= 0 ? (JSON.parse(m.slice(i)).detail ?? null) : null;
  } catch (_) { return null; }
};

const linkBtn = {
  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
  color: 'var(--accent-text)', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2,
};

// Inside a drawer of the v2 person page (views/contact_page_v2.jsx provides
// true) Escape stays with the innermost thing — the add-email input, a
// typeahead's open list, the referral / GPP modal — and never reaches the
// drawer under it. Everywhere else (the classic and phone pages, the GPP grid)
// it is false and Escape behaves as it always has.
const ContactDrawerScope = React.createContext(false);

const ContactEmailRows = ({ contact, Row, onRefresh }) => {
  const contactId = contact.id;
  const aliases = contact._raw?.emails || [];
  const inDrawer = React.useContext(ContactDrawerScope);
  const [adding, setAdding]     = React.useState(false);
  const [value, setValue]       = React.useState('');
  const [busy, setBusy]         = React.useState(false);
  const [conflict, setConflict] = React.useState(null);   // 409 detail

  const reset = () => { setAdding(false); setValue(''); setConflict(null); };

  const add = async (e) => {
    e && e.preventDefault && e.preventDefault();
    const email = value.trim();
    if (!email || busy) return;
    setBusy(true);
    try {
      const r = await window.AtlasApi.addContactEmail(contactId, { email });
      const n = r?.relinked_notes || 0;
      pushToast({ type: 'success', title: 'Email added',
                  body: n ? `${n} synced ${n === 1 ? 'note' : 'notes'} from that address now ${n === 1 ? 'points' : 'point'} here.` : undefined });
      reset();
      onRefresh && await onRefresh();
    } catch (err) {
      const detail = apiErrDetail(err);
      if (apiErrStatus(err) === 409 && detail && detail.contact) {
        setConflict(detail);
      } else {
        pushToast({ type: 'error', title: 'Could not add email',
                    body: typeof detail === 'string' ? detail : err.message });
      }
    } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.removeContactEmail(contactId, a.id);
      pushToast({ type: 'success', title: 'Email removed' });
      onRefresh && await onRefresh();
    } catch (err) {
      pushToast({ type: 'error', title: 'Could not remove email', body: err.message });
    } finally { setBusy(false); }
  };

  // The 409 path: merge the other contact INTO this one — its notes, tags
  // and addresses move here and its record is hidden.
  const mergeOther = async () => {
    const other = conflict && conflict.contact;
    if (!other || busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.mergeContacts(contactId, other.id);
      pushToast({ type: 'success', title: 'Contacts merged',
                  body: `${other.full_name || 'The other contact'} now lives on this record.` });
      reset();
      onRefresh && await onRefresh();
    } catch (err) {
      pushToast({ type: 'error', title: 'Merge failed', body: apiErrDetail(err) || err.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      {aliases.map((a, i) => (
        <Row key={a.id} label={i === 0 ? 'Also' : ''} value={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
            <a href={`mailto:${a.email}`} style={{ color: 'var(--text)', textDecoration: 'none',
                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                 // Phone: a 44px line (the negative margin keeps the row height).
                 ...(rowPhone() ? { display: 'inline-block', lineHeight: '44px', margin: '-12px 0' } : null) }}
               title={a.label || (a.source === 'merge' ? 'Came from a merged duplicate' : 'Additional address')}>
              {a.email}
            </a>
            {a.source === 'merge' && <Pill hue="slate" sz="sm">merged</Pill>}
            <button onClick={() => remove(a)} disabled={busy} title="Remove this address"
                    style={{ ...linkBtn, color: 'var(--text-4)', textDecoration: 'none', fontSize: 11,
                             ...(rowPhone() ? { width: 44, height: 44, flexShrink: 0, fontSize: 14 } : null) }}>✕</button>
          </span>
        } />
      ))}
      <Row label={aliases.length ? '' : 'Also'} clip={false} value={
        adding ? (
          // Two lines: the 300px panel leaves ~170px beside the label, too
          // narrow for an address AND its buttons on one row.
          <form onSubmit={add} style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '2px 0' }}>
            <input autoFocus type="email" value={value} disabled={busy}
                   onChange={(e) => { setValue(e.target.value); setConflict(null); }}
                   onKeyDown={(e) => { if (e.key === 'Escape') { if (inDrawer) e.stopPropagation(); reset(); } if (e.key === 'Enter') add(e); }}
                   placeholder="name@other-domain.com"
                   style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '3px 6px',
                            border: '1px solid var(--border)', borderRadius: 4,
                            background: 'var(--surface)', color: 'var(--text)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Btn renders a <button> with the HTML default type, which submits the form. */}
              <Btn size="xs" variant="outline" disabled={busy || !value.trim()}>Add</Btn>
              <button type="button" onClick={reset} style={{ ...linkBtn, color: 'var(--text-3)' }}>Cancel</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setAdding(true)} style={{ ...linkBtn, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}
                  title="Another address this person writes from — emails and meetings from it will link here">
            {window.PhoneHit && <window.PhoneHit />}
            + add email
          </button>
        )
      } />
      {conflict && conflict.contact && (
        <div style={{ margin: '2px 14px 6px', padding: '8px 10px', borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--surface-2, var(--surface))',
                      fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.45 }}>
          <div>
            That address already belongs to{' '}
            <button onClick={() => window.AtlasNav?.goToContact(conflict.contact.id)} style={{ ...linkBtn, fontSize: 11.5, fontWeight: 600 }}>
              {conflict.contact.full_name || 'another contact'}
            </button>
            {conflict.contact.company_name ? ` · ${conflict.contact.company_name}` : ''}
            {conflict.where === 'alias' ? ' (as an additional address)' : ''}.
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Btn size="xs" onClick={mergeOther} disabled={busy}>Merge them into this contact</Btn>
            <span style={{ color: 'var(--text-3)' }}>Their notes, tags and addresses move here.</span>
            <button onClick={() => setConflict(null)} style={{ ...linkBtn, color: 'var(--text-3)', fontSize: 11.5 }}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
};

// Explicit merge for duplicates found by eye (same person twice, different
// addresses — 161 such name+company groups on prod when this shipped).
const ContactMergeRow = ({ contact, onMerged, initialOpen = false }) => {
  const [open, setOpen] = React.useState(initialOpen);
  const [pick, setPick] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const cancel = () => { setOpen(false); setPick(null); };

  const doMerge = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.mergeContacts(contact.id, pick.id);
      pushToast({ type: 'success', title: 'Contacts merged',
                  body: `${pick.full_name} now lives on this record.` });
      cancel();
      onMerged && await onMerged();
    } catch (err) {
      pushToast({ type: 'error', title: 'Merge failed', body: apiErrDetail(err) || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '14px 14px 10px', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Duplicates</div>
      {!open ? (
        <button onClick={() => setOpen(true)} style={{ ...linkBtn, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}
                title="Same person under another record? Merge that record into this one.">
          {window.PhoneHit && <window.PhoneHit />}
          Merge another contact into this one
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ContactTypeahead value={pick} onChange={setPick} />
          {pick && pick.id === contact.id && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>That is this contact — pick the duplicate.</div>
          )}
          {pick && pick.id !== contact.id && (
            <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.45 }}>
              <b>{pick.full_name}</b>'s notes, tags and email addresses move here; their record is hidden in Atlas.
              {' '}If both are in DealCloud they're merged there too, and DealCloud deletes the duplicate.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn size="xs" onClick={doMerge} disabled={busy || !pick || pick.id === contact.id}>Merge</Btn>
            <button onClick={cancel} style={{ ...linkBtn, color: 'var(--text-3)' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// One row of the properties panel below (also handed to ContactEmailRows and
// LeadSourceFields). Module scope so its identity is stable across the
// panel's renders; `rs` is the panel's research controller, read only when
// the row has `research`.
// clip=false lets a row's value overflow the cell — needed for the Company
// row, whose typeahead dropdown would otherwise be cut off by the ellipsis.
const rowPhone = () => !!(window.AtlasDevice && window.AtlasDevice.phone);   // All details on a phone: finger-sized rows
const Row = ({ label, value, clip = true, research, rs }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: rowPhone() ? '0 0 0 16px' : '6px 14px', minHeight: rowPhone() ? 48 : 30 }}>
    <span style={{ width: 110, fontSize: rowPhone() ? 14 : 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
    <span style={{ flex: 1, fontSize: rowPhone() ? 15 : 12, color: 'var(--text)', ...(clip ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      ...(research ? { display: 'inline-flex', alignItems: 'center', gap: 5 } : {}),
      ...(rowPhone() ? { alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 5, minHeight: 48, paddingRight: 16 } : {}) }}>
      {value == null || value === '' || value === '—'
        ? <span style={{ color: 'var(--text-4)' }}>—</span>
        : value}
      {research && <window.ResearchField ctl={rs} label={label} {...research} />}
    </span>
  </div>
);

const ContactPropertiesPanel = ({ contact, company, users, typeOptions, onSave, onSaveCompany, onOpenCompany, onRefresh }) => {
  const EditableCell = window.EditableCell;
  // Every Contact field is DC-mapped; warn before opening the editor.
  const dcLocked = Boolean(contact._raw?.dealcloud_id);
  // Research wand (views/research.jsx): `research` = { field, kind, value,
  // onApply } puts the wand (and the ✨ badge once used) after the value.
  const rs = window.useFieldResearch('contact', contact._raw?.id);
  const rf = (field, kind) => ({
    field, kind, value: contact._raw?.[field], onApply: (v) => onSave({ [field]: v }),
  });

  return (
    <div>
      <div style={{ padding: '12px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact</div>
      <Row label="First name" value={<EditableCell type="text" value={contact._raw?.first_name || ''} locked={dcLocked} onSave={(first_name) => onSave({ first_name })} />} />
      <Row label="Last name"  rs={rs} research={rf('last_name')} value={<EditableCell type="text" value={contact._raw?.last_name  || ''} locked={dcLocked} onSave={(last_name)  => onSave({ last_name })}  />} />
      <Row label="Title"      rs={rs} research={rf('title')} value={<EditableCell type="text" value={contact._raw?.title      || ''} locked={dcLocked} onSave={(title)      => onSave({ title })}      />} />
      <Row label="Email"      rs={rs} research={rf('email', 'email')} value={<EditableCell type="email" value={contact._raw?.email     || ''} locked={dcLocked} onSave={(email)      => onSave({ email })}      />} />
      {/* Additional addresses (contact_emails) — Atlas-only, so no DC lock.
          Anything the person sends from these resolves to this record. */}
      {!contact._raw?.is_duplicate && <ContactEmailRows contact={contact} Row={Row} onRefresh={onRefresh} />}
      {/* The number stays click-to-edit; beside it (not on a phone, whose
          page has its own Call buttons) a call link that asks first when
          the person is marked do not call. */}
      <Row label="Phone"      rs={rs} research={rf('phone', 'phone')} value={contact._raw?.phone && !devNow().phone ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <EditableCell type="text" value={contact._raw?.phone || ''} locked={dcLocked} onSave={(phone) => onSave({ phone })} />
          </span>
          {window.CallButton && (
            <window.CallButton variant="icon" phone={contact._raw.phone} doNotCall={!!contact._raw?.do_not_call} name={contact.name} />
          )}
        </span>
      ) : <EditableCell type="text" value={contact._raw?.phone      || ''} locked={dcLocked} onSave={(phone)      => onSave({ phone })}      />} />
      <Row label="LinkedIn"   rs={rs} research={rf('linkedin_url', 'linkedin')} value={<EditableCell type="url" value={contact._raw?.linkedin_url || ''} locked={dcLocked} onSave={(linkedin_url) => onSave({ linkedin_url })} />} />
      {/* Read-only: the contact type imported from DealCloud (roles are the
          derived chips in the header). */}
      <Row label="Imported type" value={<span style={{ fontSize: 12, color: 'var(--text-2)' }}>{contact._raw?.dc_contact_type || '—'}</span>} />
      {/* Atlas's own type. Editable everywhere — the hourly reconcile only
          sets it on INSERT (hardcoded 'seller'), never on update, so a manual
          edit sticks even on DC-synced contacts. Mostly meaningful for
          hand-created contacts ('professional' = wealth advisors etc.);
          synced rows all carry the bulk 'seller' default. */}
      <Row label="Type"       value={<EditableCell type="select" value={contact._raw?.contact_type || 'seller'} options={typeOptions} onSave={(contact_type) => onSave({ contact_type })} />} />
      <Row label="Owner"      value={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <EditableCell type="user" value={contact._raw?.contact_owner_id || null} users={users} fallbackLabel={contact._raw?.contact_owner_name} locked={dcLocked}
            onSave={(contact_owner_id) => window.saveOwnerWithScope({
              kind: 'contact', field: 'contact_owner_id', value: contact_owner_id, users,
              companyName: company && company.name, hasCompany: !!contact._raw?.company_id,
              save: onSave,
            })} />
          {contact._raw?.owner_locked && <window.OwnerLockHint />}
        </span>
      } />
      <Row label="Decision-maker" value={
        <button onClick={() => onSave({ is_decision_maker: !contact._raw?.is_decision_maker })} style={{
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--accent-text)', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2, ...(window.phoneHitHost ? window.phoneHitHost() : null),
        }}>{window.PhoneHit && <window.PhoneHit />}{contact._raw?.is_decision_maker ? 'Yes — un-mark' : 'No — mark as DM'}</button>
      } />
      {/* DC's plain-text Notes field on Contact Details — campaign/status
          updates live here, not as Note records. Syncs both ways. Shown as
          "Background" so it doesn't read as the Notes feed. */}
      <Row label="Background" value={<EditableCell type="multiline" label="Background" value={contact._raw?.notes || ''} locked={dcLocked} onSave={(notes) => onSave({ notes })} />} />

      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Company</div>
      <Row label="Company" clip={false} value={
        <CompanyRowEditor contact={contact} company={company}
                          onSave={onSave} onOpenCompany={onOpenCompany} />
      } />
      {company && <Row label="Industry" value={company.industry !== '—' ? <Pill hue="teal" sz="sm">{company.industry}</Pill> : '—'} />}
      {/* The lead is the COMPANY's fact — a person carries none — but the
          contact page is where an intro or an event gets recorded, so the
          rows live here too. They edit the company; the server applies the
          rules (event wins, lead date = the day it is captured). */}
      {company && onSaveCompany && window.LeadSourceFields && (
        <window.LeadSourceFields raw={company._raw || {}} onSave={onSaveCompany} Row={Row} eventLabel="Lead event" />
      )}

      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Location</div>
      <Row label="City"    rs={rs} research={rf('city')} value={<EditableCell type="text" list={window.GEO.LIST_IDS.city}    value={contact._raw?.city    || ''} locked={dcLocked} onSave={(city)    => onSave({ city })} />} />
      <Row label="State"   rs={rs} research={rf('state')} value={<EditableCell type="text" list={window.GEO.LIST_IDS.state}   value={contact._raw?.state   || ''} locked={dcLocked} onSave={(state)   => onSave({ state })} />} />
      <Row label="Country" value={<EditableCell type="text" list={window.GEO.LIST_IDS.country} value={contact._raw?.country || ''} locked={dcLocked} onSave={(country) => onSave({ country })} />} />

      <Row label="ID" value={<window.RecordId value={contact._raw?.dealcloud_id} />} />

      {!contact._raw?.is_duplicate && <ContactMergeRow contact={contact} onMerged={onRefresh} />}
    </div>
  );
};

// ============ Referral panel + edit modal ============
//
// Renders a compact card with the contact's incoming referral (date, source,
// owner, GPP, notes) or an empty-state with "Add referral". Clicking edit
// (or the empty-state button) opens a modal that handles the polymorphic
// source picker (internal user vs external contact + optional company).
//
// We keep the panel small — the contact-detail center column is also home
// to Notes + Activity, and a referral is one row of information.

// optional: render only with a referral on file (the person isn't
// 'referred') — the form still opens on createTick. cta=false hides
// "Mark as referred" (merged duplicates).
// readOnly hides Edit (the v2 Programs drawer for a viewer or a merged duplicate).
const ContactReferralPanel = ({ contact, users, onSave, setContactId, setCompanyId, optional = false, cta = true, createTick = 0, readOnly = false }) => {
  const [editing, setEditing] = React.useState(false);
  useAskTick(createTick, () => setEditing(true));
  const r = contact._raw || {};
  const hasReferral = !!(r.referral_date || r.referral_owner_id || r.referral_source_kind);
  const modal = (
    <ContactReferralEditModal
      open={editing}
      contact={contact}
      users={users}
      onClose={() => setEditing(false)}
      onSaved={() => { setEditing(false); }}
      onSave={onSave}
    />
  );
  if (optional && !hasReferral) return modal;

  // Compact source pill — clickable when we have an ID to navigate to.
  const sourcePill = () => {
    if (r.referral_source_kind === 'external' && r.referral_source_contact_id) {
      return (
        <span role="button" style={{ cursor: 'pointer' }}
              onClick={() => setContactId(r.referral_source_contact_id)}
              title="Open referring contact">
          <Pill hue="teal" sz="sm">👤 {r.referral_source_contact_name || 'External contact'}</Pill>
        </span>
      );
    }
    if (r.referral_source_kind === 'internal' && r.referral_source_user_id) {
      return <Pill hue="violet" sz="sm">🧑‍💼 {r.referral_source_user_name || 'Internal user'}</Pill>;
    }
    return <Pill hue="slate" sz="sm">source unresolved</Pill>;
  };

  return (
    <Section
      title="Referred by"
      action={
        hasReferral
          ? (readOnly ? null : <Btn size="xs" variant="outline" onClick={() => setEditing(true)}>Edit</Btn>)
          : cta && <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
              title="This contact came IN through a warm intro — record who referred them"
              onClick={() => setEditing(true)}>Mark as referred</Btn>
      }
    >
      {!hasReferral && (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
          No referral recorded.
        </div>
      )}
      {hasReferral && (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
            {r.referral_date && (
              <span style={{ color: 'var(--text-3)' }}>{window.fmtReferralDay(r.referral_date)}</span>
            )}
            {r.referral_date && <span style={{ color: 'var(--text-4)' }}>·</span>}
            <span style={{ color: 'var(--text-3)' }}>referred by</span>
            {sourcePill()}
            {r.referral_source_kind === 'external' && r.referral_source_company_id && (
              <span role="button" style={{ cursor: 'pointer' }}
                    onClick={() => setCompanyId(r.referral_source_company_id)}
                    title="Open referring company">
                <Pill hue="teal" sz="sm">🏢 {r.referral_source_company_name}</Pill>
              </span>
            )}
            {r.referral_is_gpp && <Pill hue="amber" sz="sm">GPP partner</Pill>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-3)' }}>
            <span style={{ color: 'var(--text-4)' }}>owner:</span>
            {r.referral_owner_name
              ? <UserLink id={r.referral_owner_id} name={r.referral_owner_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} />
              : <span style={{ color: 'var(--text-4)' }}>—</span>}
          </div>
          {r.referral_notes && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {r.referral_notes}
            </div>
          )}
        </div>
      )}
      {modal}
    </Section>
  );
};

const ContactReferralEditModal = ({ open, contact, users, onClose, onSaved, onSave }) => {
  const r = contact._raw || {};
  // Local draft so the user can cancel without writing. We snapshot from
  // contact on every open by keying the form effect on `open`.
  const [date, setDate] = React.useState('');
  const [ownerId, setOwnerId] = React.useState('');
  const [kind, setKind] = React.useState('');        // '' | 'internal' | 'external'
  const [sourceUser, setSourceUser] = React.useState('');
  const [sourceContact, setSourceContact] = React.useState(null); // {id, full_name, company_name}
  const [sourceCompany, setSourceCompany] = React.useState(null); // {id, name}
  const [isGpp, setIsGpp] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  // In the v2 Programs drawer Escape closes the modal before the drawer under
  // it; elsewhere (ContactDrawerScope false) the modal takes no Escape entry.
  const inDrawer = React.useContext(ContactDrawerScope);
  window.useEscapeKey(open && inDrawer, onClose);

  React.useEffect(() => {
    if (!open) return;
    setDate(r.referral_date ? r.referral_date.slice(0, 10) : '');
    setOwnerId(r.referral_owner_id || '');
    setKind(r.referral_source_kind || '');
    setSourceUser(r.referral_source_user_id || '');
    setSourceContact(r.referral_source_contact_id
      ? { id: r.referral_source_contact_id, full_name: r.referral_source_contact_name, company_name: r.referral_source_company_name }
      : null);
    setSourceCompany(r.referral_source_company_id
      ? { id: r.referral_source_company_id, name: r.referral_source_company_name }
      : null);
    setIsGpp(!!r.referral_is_gpp);
    setNotes(r.referral_notes || '');
  }, [open, contact]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true);
    try {
      // Build the patch. Sending undefined means "leave unchanged"; sending
      // null clears the column. We always send the full block so a clear
      // back to "no referral" works.
      const patch = {
        referral_date:              date ? new Date(date).toISOString() : null,
        referral_owner_id:          ownerId || null,
        referral_source_kind:       kind || null,
        referral_source_user_id:    kind === 'internal' ? (sourceUser || null) : null,
        referral_source_contact_id: kind === 'external' ? (sourceContact?.id || null) : null,
        referral_source_company_id: sourceCompany?.id || null,
        referral_is_gpp:            isGpp,
        referral_notes:             notes.trim() || null,
      };
      await onSave(patch);
      onSaved && onSaved();
    } catch (_) {
      // toast surfaced by onSave already
    } finally { setSaving(false); }
  };

  const clearAll = async () => {
    setSaving(true);
    try {
      await onSave({
        referral_date: null, referral_owner_id: null, referral_source_kind: null,
        referral_source_user_id: null, referral_source_contact_id: null,
        referral_source_company_id: null, referral_is_gpp: false, referral_notes: null,
      });
      onSaved && onSaved();
    } catch (_) {} finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
                   alignItems: 'center', justifyContent: 'center', padding: 20, ...phoneModalPad() }}>
      <div onClick={onClose}
           style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.32)' }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'relative', width: 520, maxWidth: '100%', maxHeight: phoneModalMaxH(),
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-strong)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <header style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {r.referral_date || r.referral_owner_id ? 'Edit referral' : 'Mark as referred'}
          </h2>
          <button onClick={onClose} aria-label="Close" style={{
            width: 26, height: 26, borderRadius: 5, border: 'none',
            background: 'transparent', cursor: 'pointer', color: 'var(--text-3)',
            fontSize: 18, lineHeight: 1,
          }}>×</button>
        </header>
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr'), gap: 10 }}>
            <Field label="Date">
              <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Owner (IPro dealmaker)">
              <Combobox
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                options={[
                  { value: '', label: '— pick a dealmaker —' },
                  ...users.map((u) => ({ value: u.id, label: u.full_name })),
                ]}
              />
            </Field>
          </div>

          <Field label="Source">
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { value: '',         label: 'None' },
                { value: 'external', label: 'External contact' },
                { value: 'internal', label: 'Internal user' },
              ].map((opt) => {
                const active = kind === opt.value;
                return (
                  <button
                    key={opt.value || 'none'}
                    onClick={() => setKind(opt.value)}
                    style={{
                      padding: '5px 10px', borderRadius: 5, border: '1px solid',
                      borderColor: active ? 'var(--accent-text)' : 'var(--border)',
                      background:  active ? 'var(--accent-soft)' : 'var(--surface)',
                      color:       active ? 'var(--accent-text)' : 'var(--text-2)',
                      fontSize: 12, cursor: 'pointer', fontWeight: active ? 600 : 500,
                    }}>{opt.label}</button>
                );
              })}
            </div>
          </Field>

          {kind === 'internal' && (
            <Field label="Internal user who referred">
              <Combobox
                value={sourceUser}
                onChange={(e) => setSourceUser(e.target.value)}
                options={[
                  { value: '', label: '— pick a team member —' },
                  ...users.map((u) => ({ value: u.id, label: u.full_name })),
                ]}
              />
            </Field>
          )}

          {kind === 'external' && (
            <>
              <Field label="External contact (the person who referred)" hint="Type to search — or create them right here if they're new.">
                <ContactTypeahead createType="professional" value={sourceContact} onChange={(c) => {
                  setSourceContact(c);
                  // If the contact has a company we know, prefill the company picker.
                  if (c && c.company_id && !sourceCompany) {
                    setSourceCompany({ id: c.company_id, name: c.company_name });
                  }
                }} />
              </Field>
              <Field label="Referring contact's company (optional)" hint="For roll-up reporting (e.g. 'all referrals from Raymond James').">
                <CompanyTypeahead allowCreate value={sourceCompany} onChange={setSourceCompany} />
              </Field>
            </>
          )}

          <Field label="Tags">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
              <input type="checkbox" checked={isGpp} onChange={(e) => setIsGpp(e.target.checked)} />
              GPP partner referral
            </label>
          </Field>

          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Any context worth preserving (intro thread, deal angle, partner status)"
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12.5,
                border: '1px solid var(--border)', borderRadius: 5,
                background: 'var(--surface)', color: 'var(--text)',
                fontFamily: 'inherit', resize: 'vertical',
              }} />
          </Field>
        </div>
        <footer style={{ padding: '10px 16px', borderTop: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center', gap: 8 }}>
          {(r.referral_date || r.referral_owner_id || r.referral_source_kind) && (
            <Btn size="xs" variant="ghost" onClick={clearAll} disabled={saving}
                  style={{ color: 'var(--rose-fg)', marginRight: 'auto' }}>
              Clear referral
            </Btn>
          )}
          <div style={{ flex: 1 }} />
          <Btn size="xs" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="xs" variant="accent" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </footer>
      </div>
    </div>
  );
};

// Tiny typeahead for picking an existing contact. Search-as-you-type with
// debounce; results land in a dropdown. We avoid pulling the full 10K
// contact list into the browser — searches are cheap server-side.
// `createType` (a contact_type) turns on "+ New contact" under the results —
// opens NewPersonDrawer seeded with the query, stacked above the modal
// (zIndex 70 > the modal's 60), so a referrer who isn't in Atlas yet can be
// created without leaving the Mark-as-referred form.
const ContactTypeahead = ({ value, onChange, createType }) => {
  const inDrawer = React.useContext(ContactDrawerScope);
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      window.AtlasApi.listContacts({ q: q.trim(), limit: 8 })
        .then((r) => setResults((r?.items || []).slice(0, 8)))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  if (value && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                     border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1 }}>
          {value.full_name}
          {value.company_name && (
            <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>· {value.company_name}</span>
          )}
        </span>
        <button onClick={() => { onChange(null); setOpen(true); setQ(''); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-3)',
                          cursor: 'pointer', fontSize: 11 }}>change</button>
      </div>
    );
  }

  const showCreate = createType && q.trim().length >= 2;
  // In a v2 drawer, Escape with the list open closes the list only — stopped
  // here so the document-level Escape stack (the modal's entry, the drawer's)
  // doesn't discard the form; with the list closed, Escape reaches the modal.
  const onListKey = (e) => {
    if (!inDrawer || e.key !== 'Escape' || !open || (results === null && !showCreate)) return;
    e.stopPropagation();
    setOpen(false);
  };
  return (
    <div style={{ position: 'relative' }}>
      <TextInput value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                 onFocus={() => setOpen(true)} onKeyDown={onListKey}
                 placeholder="Search contacts by name or email" />
      {open && (results !== null || showCreate) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 70,
                       background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 5, boxShadow: 'var(--shadow)', marginTop: 2,
                       maxHeight: 220, overflow: 'auto' }}>
          {(results || []).length === 0 && !showCreate && (
            <div style={{ padding: 8, fontSize: 11.5, color: 'var(--text-3)' }}>
              {q.trim().length < 2 ? 'Type at least 2 characters' : 'No matches'}
            </div>
          )}
          {(results || []).map((c) => (
            <button key={c.id}
              onClick={() => { onChange({ id: c.id, full_name: c.full_name, company_id: c.company_id, company_name: c.company_name }); setOpen(false); setQ(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 8px', fontSize: 12, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        borderBottom: '1px solid var(--border)' }}>
              <div style={{ color: 'var(--text)', fontWeight: 500 }}>{c.full_name}</div>
              {(c.company_name || c.email) && (
                <div style={{ color: 'var(--text-3)', fontSize: 11 }}>
                  {c.company_name || ''}{c.company_name && c.email ? ' · ' : ''}{c.email || ''}
                </div>
              )}
            </button>
          ))}
          {showCreate && (
            <button onClick={() => setCreating(true)}
              style={{ display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 8px', fontSize: 12, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        color: 'var(--accent-text)', fontWeight: 500 }}>
              + New contact “{q.trim()}”
            </button>
          )}
        </div>
      )}
      {creating && ReactDOM.createPortal(
        <window.NewPersonDrawer
          initialName={q.trim()}
          initialType={createType}
          zIndex={70}
          onClose={() => setCreating(false)}
          onCreated={(contact) => {
            setCreating(false);
            if (contact?.id) {
              onChange({ id: contact.id, full_name: contact.full_name,
                         company_id: contact.company_id, company_name: contact.company_name });
              setOpen(false); setQ('');
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </div>
  );
};

// `allowCreate` adds "+ New company" under the results — NewCompanyDrawer
// seeded with the query, stacked above the modal (zIndex 70).
const CompanyTypeahead = ({ value, onChange, allowCreate }) => {
  const inDrawer = React.useContext(ContactDrawerScope);
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [lookups, setLookups] = React.useState(null);  // {industries, members} — lazy

  const openCreate = () => {
    if (lookups === null) {
      Promise.all([
        window.AtlasApi.listIndustries().catch(() => []),
        window.AtlasData.loadTeamMembers().catch(() => []),
      ]).then(([industries, members]) => setLookups({
        industries: Array.isArray(industries) ? industries : [],
        members: Array.isArray(members) ? members : [],
      }));
    }
    setCreating(true);
  };

  React.useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      window.AtlasApi.listCompanies({ q: q.trim(), limit: 8 })
        .then((r) => setResults((r?.items || []).slice(0, 8)))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  if (value && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                     border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1 }}>{value.name}</span>
        <button onClick={() => { onChange(null); setOpen(true); setQ(''); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-3)',
                          cursor: 'pointer', fontSize: 11 }}>change</button>
      </div>
    );
  }

  const showCreate = allowCreate && q.trim().length >= 2;
  // In a v2 drawer, Escape with the list open closes the list only (see ContactTypeahead).
  const onListKey = (e) => {
    if (!inDrawer || e.key !== 'Escape' || !open || (results === null && !showCreate)) return;
    e.stopPropagation();
    setOpen(false);
  };
  return (
    <div style={{ position: 'relative' }}>
      <TextInput value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                 onFocus={() => setOpen(true)} onKeyDown={onListKey}
                 placeholder="Search companies by name" />
      {open && (results !== null || showCreate) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 70,
                       background: 'var(--surface)', border: '1px solid var(--border)',
                       borderRadius: 5, boxShadow: 'var(--shadow)', marginTop: 2,
                       maxHeight: 220, overflow: 'auto' }}>
          {(results || []).length === 0 && !showCreate && (
            <div style={{ padding: 8, fontSize: 11.5, color: 'var(--text-3)' }}>
              {q.trim().length < 2 ? 'Type at least 2 characters' : 'No matches'}
            </div>
          )}
          {(results || []).map((c) => (
            <button key={c.id}
              onClick={() => { onChange({ id: c.id, name: c.name }); setOpen(false); setQ(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 8px', fontSize: 12, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        borderBottom: '1px solid var(--border)' }}>
              <div style={{ color: 'var(--text)', fontWeight: 500 }}>{c.name}</div>
            </button>
          ))}
          {showCreate && (
            <button onClick={openCreate}
              style={{ display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 8px', fontSize: 12, border: 'none',
                        background: 'transparent', cursor: 'pointer',
                        color: 'var(--accent-text)', fontWeight: 500 }}>
              + New company “{q.trim()}”
            </button>
          )}
        </div>
      )}
      {creating && ReactDOM.createPortal(
        <window.NewCompanyDrawer
          industries={lookups?.industries || []}
          members={lookups?.members || []}
          initialName={q.trim()}
          zIndex={70}
          onClose={() => setCreating(false)}
          onCreated={(company) => {
            setCreating(false);
            if (company?.id) {
              onChange({ id: company.id, name: company.name });
              setOpen(false); setQ('');
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </div>
  );
};

// ============ GPP partnership panel + promote/demote modal ============
//
// Lives under the Referral panel on the contact-detail center column.
// Three states:
//   1. Not a partner → "Promote to GPP partner" CTA + an empty hint.
//   2. Is a partner → compact summary (type, owner, funnel pills, refs sent)
//      with Edit / Remove buttons.
//   3. Editing → opens a modal with the full field set, including the
//      classifier dropdown to override partner_type when it landed on 'other'.

const GPP_PARTNER_TYPES = [
  { value: 'wealth_manager', label: 'Wealth manager' },
  { value: 'accountant',     label: 'Accountant' },
  { value: 'attorney',       label: 'Attorney' },
  { value: 'banker',         label: 'Banker' },
  { value: 'other',          label: 'Other' },
];
const GPP_NURTURE = [
  { value: 'active',   label: 'Active' },
  { value: 'cooling',  label: 'Cooling' },
  { value: 'cold',     label: 'Cold' },
  { value: 'inactive', label: 'Inactive' },
];
// Same vocabulary the workspace GppView uses. Free-text on the API side —
// the dropdown is convenience; users can also clear back to NULL.
// Funnel boundary (2026-09-15): 'Exploratory Call Scheduled' — the discovery
// call — is the LAST pre-program step, so it is a lead status; 'Attended
// Call - Moving Into GPP' is the FIRST in-program stage. DC files both on
// the other field; the import translates (migrate_gpp_partners.
// normalize_funnel) and 202609151800 moved the old rows.
const GPP_LEAD_STATUS = [
  'Call Scheduled', 'Follow-Up Scheduled', 'Exploratory Call Scheduled',
  'Did Not Attend Call', 'Stalled',
  'Attended Call - DQ', 'Cancelled Call - DQ',
  'Attended - Not Interested', 'Lost',
];
// In-program stages carry the Growth Partner portal's names (Zach + Chris,
// 2026-09-23): Exploratory Call Complete → Network Insights Call Complete →
// Network Matrix Call Complete → Month 1..12 Check In Complete → Fully
// Onboarded. DC's GPPStage labels are translated on import; migrations
// 202609211600 + 202609232300 renamed the existing rows.
const GPP_STAGE_VALUES = [
  'Unassigned',
  'Exploratory Call Complete', 'Network Insights Call Complete', 'Network Matrix Call Complete',
  ...Array.from({ length: 12 }, (_, i) => `Month ${i + 1} Check In Complete`),
  'Fully Onboarded',
  'Disqualified', 'Inactive',
];

// optional: render only for a partner record (the person isn't 'gpp');
// the promote form still opens on createTick. onPresence(bool) reports
// whether a record exists once known; cta=false hides "Promote to GPP";
// readOnly hides Edit / Remove (the v2 Programs drawer for a viewer or a
// merged duplicate).
const ContactGppPanel = ({ contact, users, setContactId, setCompanyId, optional = false, cta = true, createTick = 0, onPresence, allRefs = false, readOnly = false }) => {
  const [partner, setPartner] = React.useState(undefined); // undefined=loading, null=not a partner, obj=partner
  const [refs, setRefs] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [promoting, setPromoting] = React.useState(false);
  useAskTick(createTick, () => setPromoting(true));
  React.useEffect(() => {
    if (partner !== undefined && onPresence) onPresence(!!partner);
  }, [partner]);   // eslint-disable-line react-hooks/exhaustive-deps

  const contactId = contact._raw?.id;
  const isGppTagged = (contact._raw?.tags || []).some?.((t) => t.slug === 'gpp') ?? false;

  const reload = React.useCallback(() => {
    if (!contactId) return;
    setPartner(undefined); setRefs(null);
    window.AtlasApi.getGppPartnerByContact(contactId)
      .then((p) => {
        setPartner(p);   // server returns null when not a partner
        if (p && p.id) {
          window.AtlasApi.gppPartnerReferralsSent(p.id)
            .then((r) => setRefs(r?.items || []))
            .catch(() => setRefs([]));
        }
      })
      .catch(() => setPartner(null));
  }, [contactId]);
  React.useEffect(() => { reload(); }, [reload]);

  const removePartner = () => {
    showConfirm({
      title: 'Remove from GPP?',
      body: 'The contact stays, but they leave the Growth Partner program. You can add them back anytime.',
      onConfirm: async () => {
        try {
          await window.AtlasApi.removeGppPartner(partner.id);
          pushToast({ type: 'success', title: 'Removed from GPP' });
          reload();
        } catch (e) {
          pushToast({ type: 'error', title: 'Remove failed', body: e.message });
        }
      },
    });
  };

  // Promote OR edit — same modal, differs only in whether `partner` is
  // null at open. The form resets on each open via useEffect.
  const modal = (
    <GppPartnerEditModal
      open={editing || promoting}
      mode={editing ? 'edit' : 'promote'}
      partner={partner}
      contact={contact}
      users={users}
      onClose={() => { setEditing(false); setPromoting(false); }}
      onSaved={() => { setEditing(false); setPromoting(false); reload(); }}
    />
  );
  if (optional && !partner) return modal;

  return (
    <Section
      title="Growth Partner Program"
      action={partner ? (readOnly ? null : (
        <div style={{ display: 'flex', gap: 4 }}>
          <Btn size="xs" variant="outline" onClick={() => setEditing(true)}>Edit</Btn>
          <Btn size="xs" variant="ghost" onClick={removePartner} style={{ color: 'var(--rose-fg)' }}>Remove</Btn>
        </div>
      )) : cta ? (
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={() => setPromoting(true)}>
          Promote to GPP
        </Btn>
      ) : null}
    >
      {partner === undefined && (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      )}

      {partner === null && (
        <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-3)' }}>
          {isGppTagged ? 'GPP tag · no partner record.' : 'Not a Growth Partner.'}
        </div>
      )}

      {partner && (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
            <Pill hue="teal" sz="sm">
              {GPP_PARTNER_TYPES.find((t) => t.value === partner.partner_type)?.label || partner.partner_type}
            </Pill>
            <Pill hue={
              partner.nurture_status === 'active'   ? 'green' :
              partner.nurture_status === 'cooling'  ? 'amber' :
              partner.nurture_status === 'cold'     ? 'rose'  : 'slate'
            } sz="sm">{partner.nurture_status}</Pill>
            {partner.partner_type === 'other' && (
              // Highlight unclassified partners so the user knows to fix it.
              // ~133 of 355 fell here on initial import (pattern matcher missed
              // small / idiosyncratic firm names).
              <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontStyle: 'italic' }}>
                · classify ↗
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-4)' }}>owner:</span>
            <UserLink id={partner.assigned_dealmaker_id} name={partner.dealmaker_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} />
            {partner.number_of_business_owners && (
              <>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span>{partner.number_of_business_owners} biz owners</span>
              </>
            )}
            {partner.gpp_call_attended === false && (
              <Pill hue="rose" sz="sm">no-show</Pill>
            )}
          </div>

          {(partner.referral_lead_status || partner.gpp_stage || partner.call_date) && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', fontSize: 11.5 }}>
              {partner.referral_lead_status && (
                <Pill hue="slate" sz="sm">Lead: {partner.referral_lead_status}</Pill>
              )}
              {partner.gpp_stage && (
                <Pill hue="violet" sz="sm">Stage: {partner.gpp_stage}</Pill>
              )}
              {partner.call_date && (() => {
                const day = partner.call_date.slice(0, 10);
                const upcoming = day > window.localISODate();
                const label = new Date(day + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
                return <Pill hue={upcoming ? 'amber' : 'slate'} sz="sm">{upcoming ? 'Call booked: ' : 'Call: '}{label}</Pill>;
              })()}
            </div>
          )}

          {/* Referrals sent BY this partner — pulled lazily from /referrals-sent.
              This is the partnership's ROI. Click a row to open that referred contact. */}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                           letterSpacing: '0.05em', fontWeight: 500, marginBottom: 4 }}>
              Referrals sent ({refs === null ? '…' : refs.length})
            </div>
            {refs !== null && refs.length === 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
                No referrals from this partner yet.
              </div>
            )}
            {refs !== null && refs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {refs.slice(0, allRefs ? refs.length : 5).map((r) => (
                  <div key={r.contact_id} role="button" onClick={() => setContactId(r.contact_id)}
                       style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
                                 cursor: 'pointer', color: 'var(--text-2)', ...(rowPhone() ? { minHeight: 44, fontSize: 14 } : null) }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.contact_name}
                    </span>
                    <span style={{ color: 'var(--text-4)', fontSize: 10.5 }}>
                      {r.referral_date ? window.fmtReferralDay(r.referral_date) : '—'}
                    </span>
                  </div>
                ))}
                {!allRefs && refs.length > 5 && (
                  <div style={{ fontSize: 11, color: 'var(--text-4)' }}>
                    + {refs.length - 5} more
                  </div>
                )}
              </div>
            )}
          </div>

          {partner.notes && (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
              {partner.notes}
            </div>
          )}
        </div>
      )}

      {modal}
    </Section>
  );
};

const GppPartnerEditModal = ({ open, mode, partner, contact, users, onClose, onSaved }) => {
  // Local form state — initialized when the modal opens.
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  // In the v2 Programs drawer Escape closes the modal before the drawer under
  // it (the stack is LIFO); elsewhere — the classic page, the GPP grid — the
  // modal takes no Escape entry (ContactDrawerScope false).
  const inDrawer = React.useContext(ContactDrawerScope);
  window.useEscapeKey(open && inDrawer, onClose);

  React.useEffect(() => {
    if (!open) return;
    setError('');
    if (mode === 'edit' && partner) {
      setForm({
        assigned_dealmaker_id: partner.assigned_dealmaker_id || '',
        partner_type:          partner.partner_type || 'other',
        nurture_status:        partner.nurture_status || 'cooling',
        company_name:          partner.company_name || '',
        referral_lead_status:  partner.referral_lead_status || '',
        gpp_stage:             partner.gpp_stage || '',
        number_of_business_owners: partner.number_of_business_owners || '',
        gpp_call_attended:     partner.gpp_call_attended,
        joined_date:           partner.joined_date ? partner.joined_date.slice(0, 10) : '',
        last_contact_date:     partner.last_contact_date ? partner.last_contact_date.slice(0, 10) : '',
        call_date:             partner.call_date ? partner.call_date.slice(0, 10) : '',
        notes:                 partner.notes || '',
      });
    } else {
      // Promote: prefill from the contact's company + a sensible default owner.
      // We deliberately don't pre-pick Johnny — let the user choose, since the
      // partner-owner is meaningful (often it's the dealmaker who built the
      // relationship, not the program lead).
      setForm({
        assigned_dealmaker_id: '',
        partner_type:          'other',
        nurture_status:        'cooling',
        company_name:          contact?._raw?.company_name || '',
        referral_lead_status:  '',
        gpp_stage:             '',
        number_of_business_owners: '',
        gpp_call_attended:     null,
        joined_date:           '',
        last_contact_date:     '',
        call_date:             '',
        notes:                 '',
      });
    }
  }, [open, mode, partner, contact]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const patch = {
        assigned_dealmaker_id: form.assigned_dealmaker_id || null,
        partner_type:          form.partner_type,
        nurture_status:        form.nurture_status,
        company_name:          form.company_name?.trim() || null,
        referral_lead_status:  form.referral_lead_status || null,
        gpp_stage:             form.gpp_stage || null,
        number_of_business_owners: form.number_of_business_owners || null,
        gpp_call_attended:     form.gpp_call_attended,
        joined_date:           form.joined_date || null,
        last_contact_date:     form.last_contact_date || null,
        call_date:             form.call_date || null,
        notes:                 form.notes?.trim() || null,
      };
      if (mode === 'promote') {
        if (!form.assigned_dealmaker_id) {
          setError('Pick an owner before promoting.');
          setSaving(false); return;
        }
        await window.AtlasApi.createGppPartner({
          contact_id: contact._raw?.id,
          ...patch,
        });
        pushToast({ type: 'success', title: 'Promoted to GPP' });
      } else {
        await window.AtlasApi.updateGppPartner(partner.id, patch);
        pushToast({ type: 'success', title: 'Saved' });
      }
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
                   alignItems: 'center', justifyContent: 'center', padding: 20, ...phoneModalPad() }}>
      <div onClick={onClose}
           style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.32)' }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'relative', width: 560, maxWidth: '100%', maxHeight: phoneModalMaxH(),
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-strong)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <header style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {mode === 'promote' ? 'Promote to GPP partner' : 'Edit GPP partner'}
          </h2>
          <button onClick={onClose} aria-label="Close" style={{
            width: 26, height: 26, borderRadius: 5, border: 'none',
            background: 'transparent', cursor: 'pointer', color: 'var(--text-3)',
            fontSize: 18, lineHeight: 1,
          }}>×</button>
        </header>
        <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
          {error && (
            <div style={{ fontSize: 12, color: 'var(--rose-fg)', background: 'var(--rose-bg)',
                           padding: '8px 10px', borderRadius: 5, marginBottom: 10 }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr'), gap: 10 }}>
            <Field label="Owner (IPro dealmaker)">
              <Combobox
                value={form.assigned_dealmaker_id || ''}
                onChange={(e) => setForm({ ...form, assigned_dealmaker_id: e.target.value })}
                options={[
                  { value: '', label: '— pick an owner —' },
                  ...users.map((u) => ({ value: u.id, label: u.full_name })),
                ]}
              />
            </Field>
            <Field label="Partner type" hint={form.partner_type === 'other' ? 'Classify when you know the firm.' : null}>
              <Combobox
                value={form.partner_type}
                onChange={(e) => setForm({ ...form, partner_type: e.target.value })}
                options={GPP_PARTNER_TYPES}
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr'), gap: 10 }}>
            <Field label="Nurture status">
              <Combobox
                value={form.nurture_status}
                onChange={(e) => setForm({ ...form, nurture_status: e.target.value })}
                options={GPP_NURTURE}
              />
            </Field>
            <Field label="Firm">
              <TextInput value={form.company_name} placeholder="Edward Jones, Morgan Stanley, …"
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr'), gap: 10 }}>
            <Field label="Lead status (pre-program)">
              <Combobox
                value={form.referral_lead_status}
                onChange={(e) => setForm({ ...form, referral_lead_status: e.target.value })}
                options={[{ value: '', label: '— none —' }, ...GPP_LEAD_STATUS.map((s) => ({ value: s, label: s }))]}
              />
            </Field>
            <Field label="GPP stage (in-program)">
              <Combobox
                value={form.gpp_stage}
                onChange={(e) => setForm({ ...form, gpp_stage: e.target.value })}
                options={[{ value: '', label: '— none —' }, ...GPP_STAGE_VALUES.map((s) => ({ value: s, label: s }))]}
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr'), gap: 10 }}>
            <Field label="# business owners">
              <Combobox
                value={form.number_of_business_owners}
                onChange={(e) => setForm({ ...form, number_of_business_owners: e.target.value })}
                options={[
                  { value: '',     label: '— unknown —' },
                  { value: '0',    label: '0' },
                  { value: '1-10', label: '1-10' },
                  { value: '11+',  label: '11+' },
                  { value: '20+',  label: '20+' },
                ]}
              />
            </Field>
            <Field label="GPP call attended">
              <Combobox
                value={form.gpp_call_attended === null || form.gpp_call_attended === undefined ? '' :
                       form.gpp_call_attended ? 'yes' : 'no'}
                onChange={(e) => setForm({
                  ...form,
                  gpp_call_attended: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null,
                })}
                options={[
                  { value: '',    label: '— unknown —' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no',  label: 'No' },
                ]}
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phoneGrid('1fr 1fr 1fr'), gap: 10 }}>
            {/* The GPP call is booked when the partner is created — usually a
                future date — so it gets its own field (202609151900). */}
            <Field label="GPP call date">
              <TextInput type="date" value={form.call_date}
                onChange={(e) => setForm({ ...form, call_date: e.target.value })} />
            </Field>
            <Field label="Joined date">
              <TextInput type="date" value={form.joined_date}
                onChange={(e) => setForm({ ...form, joined_date: e.target.value })} />
            </Field>
            <Field label="Last contact date">
              <TextInput type="date" value={form.last_contact_date}
                onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })} />
            </Field>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder="Why they're in the program, history, …"
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12.5,
                border: '1px solid var(--border)', borderRadius: 5,
                background: 'var(--surface)', color: 'var(--text)',
                fontFamily: 'inherit', resize: 'vertical',
              }} />
          </Field>
        </div>
        <footer style={{ padding: '10px 16px', borderTop: '1px solid var(--border)',
                          display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn size="xs" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="xs" variant="accent" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : (mode === 'promote' ? 'Promote' : 'Save')}
          </Btn>
        </footer>
      </div>
    </div>
  );
};

// ── Brand Builder list membership (referral partners) ────────────────────
// Compact card mirroring the GPP panel: membership state + owner + added
// date when on the list, otherwise a one-click "Add to Brand Builder list"
// with an owner picker defaulting to the viewer. Adding round-trips to
// DealCloud (campaign + Referral Group tags, BrandListAddedDate) via the
// write-back outbox. Meetings are logged as ordinary notes with subtype
// "Referral Partner Meeting" — the panel reminds users of that.
// optional: render only for a list row (the person isn't a
// 'referral_partner') or while the add form is open (createTick opens it).
// onPresence(bool) reports whether a row exists once known. cta=false hides
// the add / log buttons; logReferral=false drops "Log referral they made"
// (the desktop page has it in the header or the ⋯ menu); readOnly hides
// Remove (the v2 Programs drawer for a viewer or a merged duplicate).
const ContactReferralPartnerPanel = ({ contact, users, activeUser, optional = false, cta = true, logReferral = true, createTick = 0, onPresence, readOnly = false }) => {
  const [rp, setRp] = React.useState(undefined); // undefined=loading, null=not on the list
  const [adding, setAdding] = React.useState(false);
  const [logging, setLogging] = React.useState(false); // "Log referral" — a referral this contact MADE
  const [ownerId, setOwnerId] = React.useState(activeUser?.id || '');
  const [busy, setBusy] = React.useState(false);
  const contactId = contact._raw?.id;
  // The ⋯ menu opens the inline add form — scrolled into view, since this
  // panel sits below the others in the column.
  const formRef = React.useRef(null);
  const askedRef = React.useRef(false);
  useAskTick(createTick, () => { askedRef.current = true; setAdding(true); });
  React.useEffect(() => {
    if (!adding || !askedRef.current || !formRef.current) return;
    askedRef.current = false;
    try { formRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) { /* no smooth scroll */ }
  }, [adding, createTick]);

  const reload = React.useCallback(() => {
    if (!contactId) return;
    setRp(undefined);
    window.AtlasApi.getReferralPartnerByContact(contactId)
      .then(setRp)
      .catch(() => setRp(null));
  }, [contactId]);
  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    if (rp !== undefined && onPresence) onPresence(!!rp);
  }, [rp]);   // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.createReferralPartner({
        contact_id: contactId,
        owner_id: ownerId || null,
      });
      pushToast({ type: 'success', title: 'Added to Brand Builder list' });
      setAdding(false);
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Add failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    showConfirm({
      title: 'Remove from Brand Builder list?',
      body: 'Soft-removes the Atlas row. DC tags are not removed automatically.',
      onConfirm: async () => {
        try {
          await window.AtlasApi.removeReferralPartner(rp.id);
          pushToast({ type: 'success', title: 'Removed from the list' });
          reload();
        } catch (e) {
          pushToast({ type: 'error', title: 'Remove failed', body: e.message });
        }
      },
    });
  };

  const fmt = (iso) => iso
    ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  if (optional && !rp && !adding) return null;

  return (
    <Section
      title="Brand Builder list"
      action={(
        <span style={{ display: 'inline-flex', gap: 6 }}>
          {cta && logReferral && (
            <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
              title={`Record a referral ${contact.name} made — who they introduced to IndustryPro`}
              onClick={() => setLogging(true)}>
              Log referral they made
            </Btn>
          )}
          {rp && rp.status === 'active' ? (readOnly ? null : (
            <Btn size="xs" variant="ghost" onClick={remove} style={{ color: 'var(--rose-fg)' }}>Remove</Btn>
          )) : rp !== undefined && cta ? (
            <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={() => setAdding((v) => !v)}>
              Add to list
            </Btn>
          ) : null}
        </span>
      )}
    >
      {rp === undefined && (
        <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      )}

      {rp !== undefined && (!rp || rp.status !== 'active') && !adding && (
        <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-3)' }}>
          {rp && rp.status === 'removed'
            ? 'Removed from the Brand Builder list.'
            : 'Not a referral partner yet.'}
        </div>
      )}

      {adding && (!rp || rp.status !== 'active') && (
        <div ref={formRef} style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Owner (deal team)</div>
            <Combobox value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
              options={[{ value: '', label: '— no owner —' },
                        ...users.map((u) => ({ value: u.id, label: u.full_name }))]}
              style={{ minWidth: 180 }} />
          </div>
          <Btn size="sm" variant="accent" disabled={busy} onClick={add}>
            {busy ? 'Adding…' : 'Add'}
          </Btn>
        </div>
      )}

      {rp && rp.status === 'active' && (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
            <Pill hue="green" sz="sm">on the list</Pill>
            {fmt(rp.brand_list_added_date) && (
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                added {fmt(rp.brand_list_added_date)}
              </span>
            )}
            {rp.last_synced_at
              ? <Pill hue="teal" sz="sm">DC synced</Pill>
              : <Pill hue="amber" sz="sm">DC sync pending</Pill>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-4)' }}>owner:</span>
            {rp.owner_name
              ? <UserLink id={rp.owner_id} name={rp.owner_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} />
              : <span>(no owner)</span>}
            {rp.last_meeting_date && (
              <>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span>last meeting {fmt(rp.last_meeting_date)}</span>
              </>
            )}
          </div>
        </div>
      )}

      {logging && (
        <window.NewReferralDrawer
          users={users}
          activeUser={activeUser}
          initialSource={{
            id: contact._raw?.id,
            full_name: contact._raw?.full_name || contact.name,
            company_id: contact._raw?.company_id || null,
            company_name: contact._raw?.company_name || null,
          }}
          onClose={() => setLogging(false)}
          onCreated={() => {
            setLogging(false);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />
      )}
    </Section>
  );
};

// ── Outreach ──────────────────────────────────────────────────────────────
// Everything marketing did to and heard from this person, off
// GET /api/marketing/contacts/:id/outreach (app/contact_outreach.py): the
// email campaigns (which steps went out, from whom, with what subject), what
// came back — the reply's own words, its classification, bounces,
// unsubscribes — cold-call outcomes, and the status history behind the
// badge. Calls themselves (recording, transcript, score) stay in the Calls
// panel below.
//
// PSAC membership rides in the header: it mirrors the DealCloud PSAC
// report's definition (routes/marketing_board PSAC_INCLUDE_TAGS /
// PSAC_EXCLUDE_TAGS), and "Add to PSAC" writes the "Should be in PSAC" tag
// locally AND pushes it to DealCloud — the hourly tag sync treats DC's blob
// as truth, so a local-only tag wouldn't survive.
const OUTREACH_INTEREST = {
  interested: ['Interested', 'green'], meeting_request: ['Meeting request', 'green'],
  follow_up: ['Follow up later', 'blue'], future: ['Future', 'blue'],
  not_interested: ['Not interested', 'rose'],
};
const OUTREACH_STATUS_HUE = {
  responded: 'amber', call_scheduled: 'green', ds_call_held: 'green',
  not_interested: 'rose', too_small: 'slate', no_show: 'rose', future: 'blue',
  bad_contact: 'rose', unsubscribed: 'rose',
};
const fmtOutreachDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, d.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};

const InterestPill = ({ interest }) => {
  const [label, hue] = OUTREACH_INTEREST[interest] || [interest, 'slate'];
  return interest ? <Pill hue={hue} sz="sm">{label}</Pill> : null;
};

// One reply's text: three lines, click to read the rest.
const ReplyText = ({ text }) => {
  const [open, setOpen] = React.useState(false);
  const long = text.length > 220;
  return (
    <div onClick={() => long && setOpen(!open)} title={long ? (open ? 'Show less' : 'Show the whole reply') : undefined}
      style={{ marginTop: 4, padding: '6px 8px', borderLeft: '2px solid var(--border-strong, var(--border))',
               background: 'var(--surface-2)', borderRadius: 4, fontSize: 12, color: 'var(--text-2)',
               whiteSpace: 'pre-wrap', cursor: long ? 'pointer' : 'default',
               ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
      {text}
    </div>
  );
};

const OutreachItem = ({ item }) => {
  const line = (label, rest) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, minWidth: 0 }}>
      <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, width: 60 }}>
        {fmtOutreachDate(item.at)}
      </span>
      <span style={{ fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        {rest}
      </span>
    </div>
  );
  switch (item.type) {
    case 'sent':
      return line(item.step ? `Step ${item.step}${item.step === 1 ? ' · initial' : ''}` : 'Email',
                  item.subject ? `“${item.subject}”` : '');
    case 'replied':
      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {line('Replied', item.subject ? `“${item.subject}”` : '')}
            <InterestPill interest={item.interest} />
          </div>
          {item.text && <div style={{ marginLeft: 66 }}><ReplyText text={item.text} /></div>}
        </div>
      );
    case 'interest_classified':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {line('Classified', '')}<InterestPill interest={item.interest} />
        </div>
      );
    case 'bounced': return line('Bounced', item.reason || 'address rejected');
    case 'unsubscribed': return line('Unsubscribed', '');
    case 'autoresponse': return line('Auto-reply', 'out of office or similar');
    case 'sequence_paused': return line('Paused', '');
    case 'sequence_resumed': return line('Resumed', '');
    case 'sequence_finished': return line('Sequence ended', '');
    default: return line(item.type, '');
  }
};

const OutreachCampaign = ({ c }) => (
  <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 5 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <Icon.Mail size={12} />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{c.name}</span>
      {c.broadcast && <Pill hue="slate" sz="sm">Buyer blast</Pill>}
      {c.channel === 'linkedin' && <Pill hue="blue" sz="sm">LinkedIn</Pill>}
      <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
        {c.senders.length ? `from ${c.senders.join(', ')} · ` : ''}
        {c.sent} sent{c.opened ? ` · ${c.opened} open${c.opened === 1 ? '' : 's'}` : ''}
        {c.clicked ? ` · ${c.clicked} click${c.clicked === 1 ? '' : 's'}` : ''}
        {c.replied ? ` · ${c.replied} repl${c.replied === 1 ? 'y' : 'ies'}` : ''}
      </span>
    </div>
    {c.items.map((it, i) => <OutreachItem key={`${it.type}-${it.at}-${i}`} item={it} />)}
  </div>
);

// sectionId / defaultOpen: the phone page passes its own key, folded, so it
// never reads or writes the desktop page's remembered open state.
const ContactOutreachPanel = ({ contact, sectionId = 'contact.outreach', defaultOpen = true }) => {
  const [data, setData] = React.useState(undefined); // undefined=loading, null=unavailable
  const [busy, setBusy] = React.useState(false);
  const [allCampaigns, setAllCampaigns] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const contactId = contact._raw?.id;

  React.useEffect(() => {
    if (!contactId) return;
    setData(undefined);
    window.AtlasApi.contactOutreach(contactId).then(setData).catch(() => setData(null));
  }, [contactId]);

  const addToPsac = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const psac = await window.AtlasApi.addContactToPsac(contactId);
      setData((d) => (d ? { ...d, psac } : d));
      pushToast({ type: 'success', title: 'Added to PSAC' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Add to PSAC failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const psac = data?.psac;
  const status = data?.status;
  const campaigns = data?.campaigns || [];
  const shown = allCampaigns ? campaigns : campaigns.slice(0, 3);
  const calls = data?.calls || [];
  const history = data?.history || [];
  const empty = data && !campaigns.length && !calls.length;
  // Reply text is captured from 2026-10 on; say so once, not under each reply.
  const textless = campaigns.some((c) => c.items.some((i) => i.type === 'replied' && !i.text));
  // Folded, the header reads "status · last campaign" (campaigns come
  // newest first).
  const preview = [status?.label || (empty ? 'None recorded' : null), campaigns[0] && campaigns[0].name]
    .filter(Boolean).join(' · ');

  return (
    <Section
      title="Outreach"
      collapsible id={sectionId} defaultOpen={defaultOpen}
      preview={preview}
      action={psac && !psac.has_roster_tag && psac.psac !== 'in' ? (
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} disabled={busy} onClick={addToPsac}
          title='Adds the "Should be in PSAC" tag here and in DealCloud'>
          {busy ? 'Adding…' : 'Add to PSAC'}
        </Btn>
      ) : null}
    >
      {data === undefined && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {data === null && <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-3)' }}>Outreach unavailable.</div>}

      {data && (
        <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-3)' }}>
          {status?.label
            ? <Pill hue={OUTREACH_STATUS_HUE[status.status] || 'slate'} sz="sm">{status.label}</Pill>
            : <span>No marketing status</span>}
          {status?.label && status.channel && <span>{status.channel === 'call' ? 'cold call' : status.channel}</span>}
          {status?.days != null && <span>· {status.days === 0 ? 'today' : `${status.days}d ago`}</span>}
          {status?.reason && <span>· {status.reason}</span>}
          <span style={{ flex: 1 }} />
          {psac?.psac === 'in' && <Pill hue="green" sz="sm">in PSAC</Pill>}
          {psac?.psac === 'out' && (
            <span title={`PSAC interest is vetoed by: ${(psac.excluded_by || []).join(', ')}`}>
              <Pill hue="amber" sz="sm">excluded from PSAC</Pill>
            </span>
          )}
          {/* No hover on touch — say who vetoed it in the open. */}
          {psac?.psac === 'out' && devNow().touch && (psac.excluded_by || []).length > 0 && (
            <span style={{ flexBasis: '100%', textAlign: 'right' }}>vetoed by {(psac.excluded_by || []).join(', ')}</span>
          )}
          {psac?.push_pending && <Pill hue="amber" sz="sm">DC sync pending</Pill>}
        </div>
      )}

      {empty && (
        <div style={{ padding: '0 14px 12px', fontSize: 12, color: 'var(--text-3)' }}>
          No email campaigns or cold-call outcomes recorded for this person.
        </div>
      )}

      {shown.map((c) => <OutreachCampaign key={c.key} c={c} />)}
      {textless && (
        <div style={{ padding: '0 14px 8px', fontSize: 11, color: 'var(--text-4)' }}>
          Replies before Oct 2026 were recorded without their text — read those in the SuperSend inbox.
        </div>
      )}
      {campaigns.length > 3 && (
        <div style={{ padding: '6px 14px', borderTop: '1px solid var(--border)' }}>
          <button onClick={() => setAllCampaigns(!allCampaigns)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: 'var(--text-2)' }}>
            {allCampaigns ? 'Show fewer campaigns' : `Show ${campaigns.length - 3} more campaign${campaigns.length - 3 === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {calls.length > 0 && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.Phone size={12} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>Cold calls</span>
          </div>
          {calls.map((c, i) => (
            <div key={`${c.at}-${i}`} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12 }}>
              <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', width: 60, flexShrink: 0 }}>{fmtOutreachDate(c.at)}</span>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{c.to_label || 'Status cleared'}</span>
              <span style={{ color: 'var(--text-2)' }}>
                {[c.actor, c.reason].filter(Boolean).join(' — ')}
              </span>
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)' }}>
          <button onClick={() => setShowHistory(!showHistory)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: 'var(--text-2)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
            {window.PhoneHit && <window.PhoneHit />}
            {showHistory ? '▾' : '▸'} Status history ({data.history_total})
          </button>
          {showHistory && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 }}>
              {history.map((h, i) => (
                <div key={`${h.at}-${i}`} style={{ display: 'flex', gap: 6, fontSize: 11.5 }}>
                  <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', width: 60, flexShrink: 0 }}>{fmtOutreachDate(h.at)}</span>
                  <span style={{ color: 'var(--text)' }}>→ {h.to_label || 'cleared'}</span>
                  <span style={{ color: 'var(--text-3)' }}>
                    {h.source === 'backfill' ? 'derived from DealCloud tags'
                      : h.source === 'manual' ? `set by ${h.actor}`
                      : h.source === 'phoneburner' ? 'cold call'
                      : h.source === 'supersend' ? 'email event'
                      : h.actor || h.source}
                  </span>
                </div>
              ))}
              {data.history_total > history.length && (
                <div style={{ fontSize: 11, color: 'var(--text-4)' }}>…{data.history_total - history.length} older</div>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

window.ContactDetail = ContactDetail;
window.ContactReferralPanel = ContactReferralPanel;
window.ContactGppPanel = ContactGppPanel;
window.ContactReferralPartnerPanel = ContactReferralPartnerPanel;
// Promote/edit form reused by the GPP Partners grid's "Add partner" chain.
window.GppPartnerEditModal = GppPartnerEditModal;
// Wave D (step 13): the pieces the v2 contact page (views/contact_page_v2.jsx)
// reuses verbatim — All details, Open tasks, Outreach history, the merge row,
// the company picker row, Tags, the referral modal, the RoleChips tooltips.
window.ContactPropertiesPanel = ContactPropertiesPanel;
window.ContactOpenTasks = ContactOpenTasks;
window.useContactOpenTasks = useContactOpenTasks;
window.ContactOutreachPanel = ContactOutreachPanel;
window.ContactMergeRow = ContactMergeRow;
window.CompanyRowEditor = CompanyRowEditor;
window.ContactTagsPanel = ContactTagsPanel;
window.ContactReferralEditModal = ContactReferralEditModal;
window.contactRoleDetails = contactRoleDetails;
// The v2 page's drawer bodies provide it (Escape stays with the innermost input, list or modal).
window.ContactDrawerScope = ContactDrawerScope;
